import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { clientModeField } from "../../../lib/retailAttribution";
import axios from "axios";
import { toast } from "sonner";
import QuoteCountdown, { secondsUntil } from "../delivery/QuoteCountdown";
import { formatMoney, itName, itLineTotal } from "../../../lib/retailFormat";
import { lineSpiceFor as spiceOf } from "../../../lib/retailFormat";
import TakeoutBadge from "../TakeoutBadge";
import {
  getRequiredChannels,
  buildVerificationPayload,
} from "../../../lib/checkoutVerification";
import VerificationModal from "./VerificationModal";
import { storefrontRequiresLoyaltyOtp } from "../../../lib/loyaltyOtpPolicy";
import LoyaltyRedeemPanel from "./LoyaltyRedeemPanel";
import ShipToAddressPanel from "./ShipToAddressPanel";
import StorefrontModal from "./StorefrontModal";
import HostDestinationCard from "../../hospitality/HostDestinationCard";
import { isValidEmail, isValidPhone } from "../../../lib/contactValidation";
import { logCustomerAuditEvent } from "../../../lib/customerAuditLog";
import { loadLinkSlug, saveOrderAccessToken, loadOrderAccessToken } from "../../../lib/retailSessionKeys";
import CloverTerminalPaymentDialog from "./CloverTerminalPaymentDialog";
import {
  saveRecovery as saveCloverRecovery,
  readRecovery as readCloverRecovery,
  clearRecovery as clearCloverRecovery,
} from "../../../lib/cloverRecovery";
import StripeTerminalPaymentDialog from "./StripeTerminalPaymentDialog";
import { fetchOrderSummary } from "../../../lib/retail/orderSummaryApi";
import {
  saveRecovery as saveStripeTerminalRecovery,
  readRecovery as readStripeTerminalRecovery,
  clearRecovery as clearStripeTerminalRecovery,
} from "../../../lib/stripeTerminalRecovery";
// Catalog Fulfillment Scheduling Phase 1 (Sep 2026) — the BACKEND's
// availability decision for this catalog + mode. Nothing is computed
// here; checkout re-checks server-side and fails closed on its own.
import useFulfillmentAvailability from "../../../hooks/retail/useFulfillmentAvailability";
import FulfillmentTimePicker from "./FulfillmentTimePicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RETAIL_BASE = `${BACKEND_URL}/api/retail/public`;

const TIP_OPTIONS = [
  { v: 0.10, l: "10%" },
  { v: 0.15, l: "15%" },
  { v: 0.18, l: "18%" },
  { v: 0.20, l: "20%" },
];

// False-address-drift-rejection UI fix (Aug 2026) — payment-session
// error codes that mean the ACCEPTED Local Delivery quote this attempt
// carried is no longer usable and the customer must request a fresh
// one (see `startCardPayment`'s catch block below). Never means "an
// unrelated/generic payment failure" — those still fall through to
// the ordinary retry-toast path unchanged.
const LOCAL_DELIVERY_QUOTE_INVALIDATED_CODES = new Set([
  "local_delivery_address_changed",
  "local_delivery_quote_expired",
  "local_delivery_quote_not_accepted",
  "local_delivery_quote_order_mismatch",
]);

function RowLight({ label, value }) {
  return (
    <div className="flex items-center justify-between text-stone-700">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

// Phase 3M — extracted verbatim from RetailCustomer.jsx::OrderSummaryModal.
// Tip pills, Cash/Card payment, place-order. The placeholder-phone fallback
// only fires when the active mode does NOT require contact info; this exact
// precondition keeps pickup/delivery validation behaviour identical.
export default function CheckoutModal({ slug, linkSlug = null, sessionId, tenant, catalog, cart, currency, customer, orderType, orderModes, labelFor, verifiedProof, memberRecognized, onLoyaltyVerified, onClose, onPlaced, onShippingAddressChange, hostDestination = null }) {
  const items = useMemo(() => cart?.items || [], [cart?.items]);
  const [tipPct, setTipPct] = useState(0);
  const [customTip, setCustomTip] = useState("");
  // Order Discount (Sep 2026) — the Order Summary is SERVER-AUTHORITATIVE.
  // Subtotal, tax, delivery, tip and total all come from
  // `POST /cart/quote`, priced by the backend's one totals engine with the
  // same inputs checkout uses. The browser used to compute tax as a flat
  // `subtotal * 8.25%` (ignoring tax-exempt products and per-product
  // rates) plus its own tip and total, so the summary could disagree with
  // what the order was charged. The browser now only says WHICH tip the
  // customer chose; a percentage tip is computed by the server on
  // post-discount merchandise.
  const tipRequest = useMemo(() => {
    if (tipPct === "custom") {
      const n = parseFloat(customTip);
      return { tip: Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0 };
    }
    return { tip_percent: Math.round(Number(tipPct || 0) * 10000) / 100 };
  }, [tipPct, customTip]);
  const [serverSummary, setServerSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [summaryPending, setSummaryPending] = useState(false);
  // The in-flight Order Summary request, so `submit` can wait for the
  // server's pricing instead of ever placing an order ahead of it.
  const summaryRequestRef = useRef(null);
  const hasSummaryRef = useRef(false);
  // The latest settled summary, and whether a re-price is in flight —
  // refs so `submit` can use an already-arrived price without waiting.
  const latestSummaryRef = useRef(null);
  const summaryPendingRef = useRef(false);
  const cartSignature = useMemo(
    () => items.map((it) => `${it.item_id || it.id}:${it.quantity}:${it.unit_price}`).join("|"),
    [items],
  );
  useEffect(() => {
    if (!slug || !sessionId || items.length === 0) {
      setServerSummary(null);
      summaryRequestRef.current = null;
      latestSummaryRef.current = null;
      summaryPendingRef.current = false;
      return undefined;
    }
    let cancelled = false;
    let settle;
    summaryRequestRef.current = new Promise((resolve) => { settle = resolve; });
    summaryPendingRef.current = true;
    setSummaryPending(true);
    const load = async () => {
      let summary = null;
      try {
        summary = await fetchOrderSummary({
          baseUrl: RETAIL_BASE, slug, sessionId, orderType, tipRequest,
        });
      } catch (_e) {
        summary = null;
      }
      if (!cancelled) {
        hasSummaryRef.current = !!summary;
        latestSummaryRef.current = summary;
        summaryPendingRef.current = false;
        setServerSummary(summary);
        setSummaryError(summary ? null : "We couldn't calculate your order total. Please try again.");
        setSummaryPending(false);
      }
      settle(cancelled ? undefined : summary);
    };
    // The first price is fetched at once; later re-prices (e.g. typing a
    // custom tip) are debounced.
    const timer = setTimeout(load, hasSummaryRef.current ? 250 : 0);
    return () => { cancelled = true; clearTimeout(timer); settle(undefined); };
  }, [slug, sessionId, orderType, cartSignature, tipRequest, items.length]);
  // Resolves to the server's current Order Summary (null on failure),
  // waiting out any re-price superseded while it was in flight.
  const awaitOrderSummary = useCallback(() => {
    const wait = async (attempt) => {
      const pending = summaryRequestRef.current;
      if (!pending) return null;
      const summary = await pending;
      if (summary === undefined && summaryRequestRef.current !== pending && attempt < 5) {
        return wait(attempt + 1);
      }
      return summary || null;
    };
    return wait(0);
  }, []);
  const summaryReady = serverSummary !== null;
  const subtotal = Number(serverSummary?.subtotal ?? cart?.subtotal ?? 0);
  const tax = Number(serverSummary?.tax ?? 0);
  const tip = Number(serverSummary?.tip ?? 0);
  const taxLabel = serverSummary?.uniform_tax_rate
    ? `Tax (${Number((Number(serverSummary.uniform_tax_rate) * 100).toFixed(3))}%)`
    : "Tax";
  // Parcel Shipping Readiness & Checkout Completion — Phase 4. Prefill
  // the "Ship to My Address" form from whatever's already known: a
  // shipping address entered earlier THIS session (survives this modal
  // being closed/reopened, since it's persisted one level up alongside
  // `customer`), or failing that, the name/email/phone already captured
  // at Welcome so the customer isn't retyping those for a first-time
  // shipping address.
  const shipToAddress = useMemo(() => {
    if (customer?.shippingAddress) return customer.shippingAddress;
    if (!customer?.name && !customer?.email && !customer?.phone) return null;
    return { name: customer.name || "", email: customer.email || "", phone: customer.phone || "" };
  }, [customer]);
  // Phase 7 — safe observability. Fires once per checkout open: either
  // confirms the address WelcomeModal validated actually made it here
  // (autofill working as intended), or flags the exact regression this
  // event exists to catch — a shipping order that reached checkout with
  // no address at all, which should be structurally impossible now that
  // Phase 4 requires + validates the address before product selection.
  useEffect(() => {
    if (orderType !== "shipping") return;
    if (customer?.shippingAddress) {
      logCustomerAuditEvent(slug, { event: "checkout_received_validated_address", orderType, catalogId: catalog?.id, sessionId, customerId: customer?.id });
    } else {
      logCustomerAuditEvent(slug, { event: "modal_unexpected_reset", orderType, reasonCode: "checkout_open", catalogId: catalog?.id, sessionId, customerId: customer?.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // CP2.2 — Selected shipping quote (only meaningful when orderType==='shipping').
  // Captured here from ShipToAddressPanel; consumed by CP2.4 when the order
  // draft is created + Stripe session is built. Wiped on mode change.
  const [shippingSelection, setShippingSelection] = useState(null);
  useEffect(() => {
    if (orderType !== "shipping" && shippingSelection) setShippingSelection(null);
  }, [orderType, shippingSelection]);
  // Parcel Shipping Checkout Enforcement — Phase 9 observability. A
  // specific carrier/service being selected, and the resulting charge
  // actually being applied to the customer-visible total, are two
  // distinct safe events a future deviation check can watch for
  // (e.g. "a shipping order reached payment whose total never
  // reflected any selected service").
  // Local Delivery Quote/Fee Checkout Enforcement — the customer's
  // in-flight Uber Direct quote for THIS checkout attempt. `null`
  // until a quote is successfully requested (see `requestLocalDelivery
  // Quote` below); once set, the modal shows the fee-confirmation view
  // in place of the normal summary/payment-button view until the
  // customer explicitly accepts (or cancels back to edit the order).
  // Wiped on mode change for the same reason `shippingSelection` is.
  const [localDeliveryQuote, setLocalDeliveryQuote] = useState(null);
  const [localDeliveryQuoteBusy, setLocalDeliveryQuoteBusy] = useState(false);
  // The draft order + its original checkout payload, captured at the
  // moment the quote was requested — needed by the "Accept & Pay"
  // button to resume the payment-session call afterward without
  // re-running checkout (which would create a second draft order).
  const [pendingLocalDeliveryOrder, setPendingLocalDeliveryOrder] = useState(null);
  const [pendingLocalDeliveryPayload, setPendingLocalDeliveryPayload] = useState(null);
  // False-address-drift-rejection UI fix (Aug 2026) — the accepted
  // quote's fee/state is cleared BEFORE the payment-session call fires
  // (see `confirmLocalDeliveryQuoteAndPay`), so a payment-session
  // rejection specific to that quote (address changed since
  // acceptance, quote expired, quote/order mismatch) must not silently
  // fall through to the default Order Summary view — that view has no
  // knowledge the quote was ever invalidated and would show a
  // merchandise-only total next to a fully actionable "Place Order"
  // button. This flag renders an explicit blocked state instead (see
  // the early-return render block below `if (localDeliveryQuote)`).
  const [localDeliveryNeedsNewQuote, setLocalDeliveryNeedsNewQuote] = useState(false);
  // Tenant-Review Checkout Startup Failure Correction (Aug 2026) —
  // confirmed staging defect (order UBE-ORD-000026): a Stripe-side
  // payment-start failure AFTER the customer already explicitly
  // accepted the delivery-inclusive total (via "Accept & Pay") used to
  // fall through to the generic toast + the ORIGINAL pre-quote Order
  // Summary view — showing a merchandise-only total next to a
  // "Place Order" button that, if clicked, would re-run `/checkout`
  // and create a SECOND, duplicate draft order. This state preserves
  // the already-created order + already-accepted quote so "Retry
  // Payment" can safely resume the SAME order/quote — no re-checkout,
  // no re-quote, no second tenant-facing price.
  const [deliveryPaymentFailed, setDeliveryPaymentFailed] = useState(null);
  const [deliveryPaymentRetryBusy, setDeliveryPaymentRetryBusy] = useState(false);
  useEffect(() => {
    if (orderType !== "delivery" && (localDeliveryQuote || localDeliveryNeedsNewQuote || deliveryPaymentFailed)) {
      setLocalDeliveryQuote(null);
      setPendingLocalDeliveryOrder(null);
      setPendingLocalDeliveryPayload(null);
      setLocalDeliveryNeedsNewQuote(false);
      setDeliveryPaymentFailed(null);
    }
  }, [orderType, localDeliveryQuote, localDeliveryNeedsNewQuote, deliveryPaymentFailed]);

  const handleShippingSelectionChange = useCallback((next) => {
    setShippingSelection(next);
    if (next?.quote_id) {
      logCustomerAuditEvent(slug, {
        event: "shipping_service_selected", orderType,
        catalogId: catalog?.id, sessionId, customerId: customer?.id,
        meta: { speed_band: next.speed_band || undefined },
      });
      logCustomerAuditEvent(slug, {
        event: "shipping_total_applied", orderType,
        catalogId: catalog?.id, sessionId, customerId: customer?.id,
      });
    }
  }, [slug, orderType, catalog?.id, sessionId, customer?.id]);
  // USPS Production Enablement M1 (Scope E) — shipping orders are card-only
  // for the same reason delivery is: no cash drawer / in-person handoff at
  // the moment of "payment", the order ships after fulfillment.
  const isRemoteFulfillment = orderType === "delivery" || orderType === "shipping";
  // Catalog Fulfillment PR 4 (Aug 2026, routing-authority corrected) —
  // Online Customer Pickup: a catalog with `pickup_requires_online_
  // card` set requires online payment, unlike the existing unpaid
  // kiosk/walk-in Pickup workflow (which keeps its own cash/card
  // choice unchanged — this flag is false for every OTHER catalog,
  // including one that never configured it at all). Independent of
  // any Local Delivery provider configuration on the same catalog.
  const isOnlineCustomerPickup = orderType === "pickup" && !!catalog?.pickup_requires_online_card;
  const [paymentMethod, setPaymentMethod] = useState((isRemoteFulfillment || isOnlineCustomerPickup) ? "card" : "cash");

  // Tenant-Review Checkout Startup Failure Correction (Aug 2026) —
  // confirmed staging defect: every payment-start-failure toast in this
  // component told the customer to "pay at the counter," including for
  // Local Delivery and Parcel Shipping — both card-only, remote
  // fulfillment modes with no in-person counter or cash drawer at all.
  // Dine-In/Takeout/Pickup keep the counter-payment suggestion, which is
  // genuinely actionable for them.
  const paymentStartFailureSuffix = useCallback((orderRef) => (
    isRemoteFulfillment
      ? `Please retry${orderRef ? ` (order ${orderRef})` : ""}.`
      : `Please retry, or pay at the counter${orderRef ? ` (order ${orderRef})` : " and reference the order shown on your receipt"}.`
  ), [isRemoteFulfillment]);
  const [submitting, setSubmitting] = useState(false);

  // Milestone 4A (Feb 2026) — Clover Station Duo terminal payment
  // eligibility. The kiosk asks the backend once per checkout open
  // whether the tenant has a connected Clover merchant AND at least
  // one active registered Station Duo. When both are true, we expose
  // "Pay at Terminal" as a third payment option alongside cash/card.
  // Delivery orders are excluded because in-person terminal payment
  // is not viable for delivery.
  const [cloverTerminalEnabled, setCloverTerminalEnabled] = useState(false);
  const [cloverDialogOpen, setCloverDialogOpen] = useState(false);
  const [cloverActiveOrder, setCloverActiveOrder] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slug || isRemoteFulfillment) {
        setCloverTerminalEnabled(false);
        return;
      }
      try {
        const { data } = await axios.get(
          `${RETAIL_BASE}/${slug}/pos/terminal-status`,
        );
        if (!cancelled) setCloverTerminalEnabled(!!data?.clover_terminal_enabled);
      } catch (_e) {
        if (!cancelled) setCloverTerminalEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, orderType, isRemoteFulfillment]);

  // Milestone 4C (Feb 2026) — durable browser-restart recovery.
  // We persist ONLY a minimal pointer in localStorage (upgraded from
  // sessionStorage in M4B so the pointer now survives a full browser
  // process restart on the physical Station Duo):
  //   { orderId, sessionId, slug, savedAt }
  // The pointer is a recovery HINT — the backend remains the
  // authoritative source of tenant, amount, order, and payment
  // status. Nothing about the payment amount, result, merchant
  // credentials, or card data is ever persisted client-side.
  //
  // All storage semantics (allow-list, tenant isolation, 24h TTL,
  // corrupt-JSON cleanup, quota-safety) live in
  // `/app/frontend/src/lib/cloverRecovery.js` and are covered by
  // 29 unit tests in `cloverRecovery.test.mjs`.
  const storageRef = React.useRef(
    typeof window !== "undefined" ? window.localStorage : null,
  );
  const saveRecovery = React.useCallback((orderId, sessionId) => {
    if (!slug || !orderId) return;
    saveCloverRecovery(storageRef.current, slug, { orderId, sessionId });
  }, [slug]);
  const clearRecovery = React.useCallback(() => {
    if (!slug) return;
    clearCloverRecovery(storageRef.current, slug);
  }, [slug]);
  const readRecovery = React.useCallback(() => {
    if (!slug) return null;
    return readCloverRecovery(storageRef.current, slug);
  }, [slug]);

  // On mount, if a recovery pointer exists, reopen the dialog against
  // the persisted order. The dialog resolves the authoritative
  // session status from the backend before rendering — no payment
  // authority ever lives in browser storage.
  React.useEffect(() => {
    if (!slug) return;
    const r = readRecovery();
    if (!r) return;
    setCloverActiveOrder({ id: r.orderId });
    setCloverDialogOpen(true);
  }, [slug, readRecovery]);

  // Stripe Terminal — Reader M2 (Sep 2026). The card-present sibling of
  // the Clover block above, and deliberately INDEPENDENT of it: a
  // tenant may run Clover, Stripe Terminal, both, or neither, and
  // enabling one must never alter the other's availability.
  //
  // The physical card interaction happens in the companion Rudras
  // Stripe Terminal Android agent on the same tablet, not in this
  // browser — see `StripeTerminalPaymentDialog.jsx`. This state only
  // tracks whether to OFFER the option and which order is in flight.
  //
  // Remote fulfillment is excluded for the same reason as Clover: a
  // delivery/shipping customer is not standing at the reader. The
  // backend enforces this independently (see
  // `payments/stripe_terminal/service._validate_order_eligibility`);
  // this filter is a UX courtesy only.
  const [stripeTerminalEnabled, setStripeTerminalEnabled] = useState(false);
  // Kiosk No-Fallback Rule (Sep 2026) — the kiosk needs to tell a reader
  // that is merely UNREACHABLE apart from one this tenant never runs, so
  // the two states below are tracked separately from the ready flag.
  //
  //   `reason: "not_configured"` — Terminal is off for this tenant. They
  //      have no reader, so their kiosk keeps Hosted Checkout.
  //   any other reason (`no_device`, `agent_offline`,
  //      `reader_disconnected`, `reader_updating`) — this tenant DOES run
  //      a reader; it just cannot take a payment this minute.
  //
  // `customer_message` is the backend's own customer-safe sentence for
  // that reason (`payments/stripe_terminal/error_codes.CUSTOMER_MESSAGES`),
  // so the kiosk never has to author copy for a state it may not fully
  // understand — including the firmware-update case, which is the one
  // state that resolves itself in a few minutes.
  const [stripeTerminalTenant, setStripeTerminalTenant] = useState(false);
  const [stripeTerminalMessage, setStripeTerminalMessage] = useState("");
  const [stripeTerminalDialogOpen, setStripeTerminalDialogOpen] = useState(false);
  const [stripeTerminalActiveOrder, setStripeTerminalActiveOrder] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slug || isRemoteFulfillment) {
        setStripeTerminalEnabled(false);
        setStripeTerminalTenant(false);
        return;
      }
      try {
        const { data } = await axios.get(
          `${RETAIL_BASE}/${slug}/pos/stripe-terminal-status`,
        );
        if (cancelled) return;
        const ready = !!data?.stripe_terminal_enabled;
        setStripeTerminalEnabled(ready);
        // Ready implies configured. Otherwise only `not_configured` means
        // "this tenant does not run a reader here".
        setStripeTerminalTenant(ready || String(data?.reason || "") !== "not_configured");
        setStripeTerminalMessage(String(data?.customer_message || ""));
      } catch (_e) {
        // A failed readiness probe must never offer an option that
        // would then time out at the counter — and it cannot prove this
        // tenant runs a reader either, so the surface falls back to what
        // it did before any of this: Hosted Checkout. The backend is
        // unreachable in this state, so the order itself would fail too.
        if (!cancelled) {
          setStripeTerminalEnabled(false);
          setStripeTerminalTenant(false);
          setStripeTerminalMessage("");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [slug, orderType, isRemoteFulfillment]);

  // Kiosk Payment Choice Reconciliation (Sep 2026) — a walk-in customer
  // standing at the kiosk is NEVER asked to type a card number, expiry
  // and CVV into the tablet. On that surface the customer-visible "Pay
  // by Card" IS the physical Stripe Reader M2, and Hosted Checkout is
  // not offered at all.
  //
  // Kiosk No-Fallback Rule (Product Owner, Sep 25 2026) — this holds
  // even when the reader cannot take a payment. A reader that is
  // offline, disconnected, out of range or updating does NOT hand the
  // customer back to card entry: "Pay by Card" is shown disabled with
  // the reason, and Cash stays available. The customer pays cash or
  // waits for the reader. An earlier revision of this PR fell back to
  // Hosted Checkout here; that fallback was rejected.
  //
  // The distinction that keeps this tenant-safe: a tenant who does not
  // run a reader at all (`reason: "not_configured"`) is NOT a kiosk
  // reader tenant, and their kiosk keeps Hosted Checkout exactly as it
  // always had. The no-fallback rule applies to tenants who run a
  // reader, which is what "the reader is unavailable" presupposes.
  //
  // Online and remote channels are untouched: the probe forces both
  // flags false for delivery and shipping, and Online Customer Pickup
  // is excluded here, so both keep Hosted Checkout exactly as before.
  const kioskSurface = !isRemoteFulfillment && !isOnlineCustomerPickup;
  const kioskReaderTenant = kioskSurface && stripeTerminalTenant;
  const kioskReaderReady = kioskReaderTenant && stripeTerminalEnabled;

  // Keep the live selection inside the contract. Two ways it can drift:
  // the readiness probe resolves AFTER first paint, so a customer can
  // have tapped Hosted Checkout in the moment before the reader was
  // known; and a reader can stop being ready after it was chosen.
  // Neither may leave a walk-in customer pointed at card entry.
  useEffect(() => {
    if (!kioskReaderTenant) return;
    if (paymentMethod === "card") {
      setPaymentMethod(kioskReaderReady ? "stripe_terminal" : "cash");
    } else if (paymentMethod === "stripe_terminal" && !kioskReaderReady) {
      setPaymentMethod("cash");
    }
  }, [kioskReaderTenant, kioskReaderReady, paymentMethod]);

  // The payment tiles this checkout offers, in display order. Built
  // here rather than inline so the grid width is derived from what is
  // actually rendered instead of a hand-maintained ternary that has to
  // be kept in step with every filter below.
  const paymentTiles = [
    { v: "cash", l: "Pay Cash", icon: "💵", bg: "border-emerald-300 bg-emerald-50/40" },
    { v: "card", l: "Pay by Card", icon: "💳", bg: "border-sky-300 bg-sky-50/40" },
    { v: "clover_terminal", l: "Pay at Terminal", icon: "🖥️", bg: "border-orange-300 bg-orange-50/40" },
    // Stripe Reader M2 — tap / chip / swipe at the counter. On the
    // kiosk this IS the customer-visible "Pay by Card": the rail name
    // is never shown to a walk-in customer, who is simply prompted by
    // the reader once the order is placed. Offered but DISABLED while
    // the reader cannot take a payment, so the option is visibly
    // unavailable rather than silently missing.
    {
      v: "stripe_terminal", l: "Pay by Card", icon: "💳",
      bg: "border-sky-300 bg-sky-50/40", disabled: !kioskReaderReady,
    },
  ]
    .filter((o) => !((isRemoteFulfillment || isOnlineCustomerPickup) && o.v === "cash"))
    // Hosted Checkout is never offered where the reader is the card rail.
    .filter((o) => o.v !== "card" || !kioskReaderTenant)
    .filter((o) => o.v !== "clover_terminal" || (cloverTerminalEnabled && !isOnlineCustomerPickup))
    .filter((o) => o.v !== "stripe_terminal" || kioskReaderTenant);

  // Durable browser-restart recovery, same contract as the Clover
  // pointer above and stored under its own namespaced key so the two
  // can never read each other's state. Semantics (allow-list, tenant
  // isolation, 24h TTL, corrupt-JSON cleanup, quota safety) live in
  // `lib/stripeTerminalRecovery.js`.
  const saveStripeRecovery = React.useCallback((orderId, sessionId) => {
    if (!slug || !orderId) return;
    saveStripeTerminalRecovery(storageRef.current, slug, { orderId, sessionId });
  }, [slug]);
  const clearStripeRecovery = React.useCallback(() => {
    if (!slug) return;
    clearStripeTerminalRecovery(storageRef.current, slug);
  }, [slug]);
  const readStripeRecovery = React.useCallback(() => {
    if (!slug) return null;
    return readStripeTerminalRecovery(storageRef.current, slug);
  }, [slug]);

  React.useEffect(() => {
    if (!slug) return;
    const r = readStripeRecovery();
    if (!r) return;
    setStripeTerminalActiveOrder({ id: r.orderId });
    setStripeTerminalDialogOpen(true);
  }, [slug, readStripeRecovery]);


  // The delivery fee is the one `services.commerce.compute_delivery_charge`
  // returns via the server Order Summary above — no browser mirror of it.
  const deliveryConfig = useMemo(() => tenant?.delivery_config || {}, [tenant?.delivery_config]);
  const delivery = Number(serverSummary?.delivery ?? 0);

  // Sprint A (Feb 2026) — verification state machine. The hook below
  // computes which channels MUST be verified for the active order mode.
  // The modal resolves each channel and yields `verifiedTokens` which is
  // forwarded verbatim into the checkout payload.
  // Go-Live Sprint (Feb 2026, item #7) — required channels are now
  // derived from the ACTIVE mode's rules + payment method. This mirrors
  // the backend `require_email_verification` gate so checkout can never
  // silently add a requirement the tenant did not opt into.
  // Delivery-phone-verification checkout fix (Aug 2026) — also passes
  // the tenant's precomputed `delivery_phone_verification_required` flag
  // (already available via `deliveryConfig` above) so Local Delivery
  // orders correctly gain an `"sms"` requirement when the tenant's
  // authoritative configuration calls for it — matching `retail/
  // orders.py`'s order-submission gate exactly.
  const requiredChannels = useMemo(
    () => getRequiredChannels(
      orderType, (orderModes || {})[orderType], paymentMethod,
      !!deliveryConfig?.delivery_phone_verification_required,
    ),
    [orderType, orderModes, paymentMethod, deliveryConfig?.delivery_phone_verification_required],
  );
  const [verifiedTokens, setVerifiedTokens] = useState({});
  const [verificationOpen, setVerificationOpen] = useState(false);
  const isFullyVerified = useMemo(() => {
    if (requiredChannels.length === 0) return true;
    return requiredChannels.every((ch) => verifiedTokens[ch]?.token);
  }, [requiredChannels, verifiedTokens]);

  // When the modal hands tokens back to us, we re-enter submit() with
  // fresh state via this effect — avoids the React stale-closure trap
  // that a `setTimeout(submit, 0)` from the modal would hit.
  const [pendingSubmitAfterVerify, setPendingSubmitAfterVerify] = useState(false);

  // Re-verification trigger: if the customer changes mid-flow (e.g.
  // swaps email), drop the existing tokens so the gate runs again.
  useEffect(() => {
    setVerifiedTokens({});
    // Sep 2026 correction — an identity change must retract everything
    // derived from the previous identity, not just the submission
    // tokens. Otherwise the points and discounted total belonging to
    // the person who was here a moment ago survive on screen, and would
    // be submitted, for whoever is here now. One rule, applied to all
    // identity-derived state: when the customer changes, it all goes.
    setLoyaltyPoints(0);
    setLoyaltySummary(null);
  }, [customer?.email, customer?.phone, orderType]);

  // After the verification modal hands us a fresh tokens map, this
  // effect fires submit() with the latest closure — sidestepping the
  // stale-closure bug we'd hit with a `setTimeout(submit, 0)` in the
  // modal's `onVerified` callback.
  // Sidesteps the stale-closure bug via a ref that always mirrors the
  // latest `submit` closure without adding it to the effect deps list.
  const submitRef = useRef(null);

  // Catalog Fulfillment Scheduling Phase 1 (Sep 2026). `blocked` is true
  // only when the backend says this catalog has scheduling enabled, the
  // customer's mode is in scope (Pickup/Takeout), and ASAP is currently
  // unavailable. Phase 1 stops the order here and tells the customer when
  // to come back; Phase 2 replaces this with a scheduled-order flow.
  const fulfillmentAvailability = useFulfillmentAvailability({
    // `linkSlug` so availability resolves the SAME catalog this
    // storefront is serving. Omitting it 404'd on a link-slug catalog
    // that had no legacy assistant link, and this hook fails open — so
    // the scheduling choice vanished silently instead of erroring.
    slug, catalogId: catalog?.id, linkSlug, orderType,
  });
  // `null` means ASAP — the customer's default and the semantic the
  // backend expects. A non-null value carries the backend's OWN canonical
  // slot instant; the label beside it is for display only and is never
  // what gets submitted.
  const [scheduledSelection, setScheduledSelection] = useState(null);
  const fulfillmentEarliestLabel = fulfillmentAvailability.earliest
    ? `${fulfillmentAvailability.earliest.label} on ${fulfillmentAvailability.earliest.local_date}`
    : "";
  const fulfillmentApplies =
    fulfillmentAvailability.schedulingEnabled
    && (fulfillmentAvailability.asapAvailable || fulfillmentAvailability.canSchedule
        || fulfillmentAvailability.blocked);
  // Blocked only when there is genuinely nothing the customer can choose:
  // ASAP is closed AND no future window is offerable. Once scheduling is
  // possible, the picker is the answer rather than a dead end.
  const fulfillmentBlocked =
    fulfillmentAvailability.blocked && !fulfillmentAvailability.canSchedule;
  // Chose "Schedule for later" but has not picked a window yet.
  const scheduledWindowMissing = scheduledSelection !== null && !scheduledSelection.start;
  useEffect(() => {
    if (pendingSubmitAfterVerify && isFullyVerified) {
      setPendingSubmitAfterVerify(false);
      void submitRef.current?.();
    }
  }, [pendingSubmitAfterVerify, isFullyVerified]);

  // USPS Production Enablement M1 (Scope E) — mirror the `delivery`
  // pattern above: the customer-visible total must include the selected
  // parcel-shipping quote, otherwise "TOTAL DUE" understates what Stripe
  // actually charges once `order_finalizer.finalize_shipping_draft()`
  // re-derives the price server-side and bumps `order.total`.
  const shippingCharge = orderType === "shipping"
    ? Number(shippingSelection?.rate_amount || 0)
    : 0;
  // The server total already includes tax, delivery and tip. A parcel
  // shipping rate is the one component the server adds only once the
  // shipping draft is finalized at payment (`order_finalizer`), so the
  // selected rate is shown on top of it, exactly as before.
  const total = Number(serverSummary?.total ?? 0) + shippingCharge;
  // Loyalty Points Phase 1 (Sep 2026) — points chosen by the member, and
  // the amount that will actually be collected by card or cash once they
  // are applied. `total` deliberately stays the order's full price: the
  // summary shows both, because showing only one would misstate either
  // what the order costs or what the customer pays today.
  //
  // Advisory only. The server re-derives every ceiling from the
  // authoritative order before reserving anything, so a tampered value
  // here is rejected at checkout rather than honoured.
  //
  // Sep 2026 (O-9) — the summary is STATE, not a ref, and losing it also
  // clears the chosen points. It was a ref read inside a `useMemo` keyed
  // only on `loyaltyPoints`, which meant a summary that later became
  // unavailable (a re-verification whose reload fails) could not
  // recompute this value: the redemption control vanished while TOTAL DUE
  // stayed discounted, showing the customer less than they were about to
  // be charged. Loyalty failing must never reduce the payable amount.
  const [loyaltySummary, setLoyaltySummary] = useState(null);
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  // Loyalty checkout visibility fix (Sep 2026) — where the redemption
  // panel's OTP proof actually comes from.
  //
  // `verifiedTokens` below is this modal's OWN map, and it is filled
  // exclusively by `VerificationModal`, which only opens from submit().
  // So while the checkout form is on screen — the one moment redemption
  // has to be offered — that map is still `{}` and
  // `buildVerificationPayload` returns null. A member who verified at
  // the welcome step was therefore shown no loyalty at all: the panel
  // received no token and issued no request.
  //
  // The page already holds that member's proof (`RetailCustomer.jsx`'s
  // `verifiedProofRef`, set on a live verification and re-established
  // from `sessionStorage` after a refresh, in both cases only once the
  // BACKEND re-validated it via `recognize()`), and now passes it in as
  // `verifiedProof`. Prefer a proof completed inside this modal when
  // there is one, since it is the freshest.
  //
  // Deliberately scoped to the loyalty panel ONLY. The order-submission
  // payload built in submit() still uses `verifiedTokens` alone, so this
  // changes nothing about which orders are allowed to be placed — it
  // only lets an already-verified member SEE the points they own. The
  // backend re-checks this token independently on every call and refuses
  // it if it is expired, revoked, or scoped to another tenant/catalog;
  // nothing here is a trust decision made in the browser.
  // Post-OTP loyalty visibility fix (Sep 2026) — why `destination_value`
  // has to travel with the proof.
  //
  // Confirmed on the real go-live tenant: a recognized member selected
  // Pay by Card, took the "Verify to view and use loyalty points" CTA,
  // received the SMS, entered the correct code — and the panel never
  // appeared. The call to action vanished (correctly: a proof now
  // existed) and nothing replaced it, with no error shown.
  //
  // The OTP was never the problem. The token was genuinely `verified`.
  // What failed was IDENTIFYING the member from it on FIRST use.
  //
  // `retail/customer_order_history.py::load_verified_customer` has two
  // paths. Given a destination channel AND value it resolves the customer
  // through `resolve_and_cache_customer` and caches the result on the
  // token row. Given no value it takes the token-only path, which
  // returns `row.get("resolved_customer_id")` — and on a brand-new token
  // nothing has cached that yet, so it returns `customer_id: None`. The
  // loyalty summary route then finds no member, answers 403, and
  // `LoyaltyRedeemPanel` renders nothing at all, because 403 is its
  // deliberately silent "not an eligible member" branch.
  //
  // This never showed up on the welcome-step OTP because that flow calls
  // `/recognize` with the destination immediately afterwards
  // (`WelcomeModal.jsx`), which performs exactly that resolution and
  // populates the cache — so by the time checkout asks, the token-only
  // path works. The checkout CTA has no such follow-up call, so its
  // token arrives at the summary endpoint unresolved and unresolvable.
  //
  // The proof already carries the value: `onLoyaltyVerified` sends
  // `destinationValue`, and `WelcomeModal`'s proof has it too. This memo
  // simply dropped it. Passing it through is the whole correction — the
  // backend then resolves the member on the first call, exactly as it
  // does for `/recognize`, and caches it for every later one.
  //
  // Scoped to a proof that ACTUALLY carries a destination, and never
  // synthesised from `customer`. That distinction is the whole safety
  // argument, and it is why only the broken case changes:
  //
  //   * a proof completed in THIS session through the CTA carries its
  //     own `destinationValue` (`onLoyaltyVerified` sends it), is brand
  //     new, and is the only proof the backend cannot yet resolve. It
  //     needs the value, and has it.
  //   * a proof RESTORED from `sessionStorage` has its destination
  //     stripped by `saveVerifiedProof` — deliberately, so no raw
  //     phone/email is ever written to browser storage on a shared
  //     kiosk. It also does not need one: the welcome-step flow that
  //     produced it called `/recognize` WITH the destination at
  //     verification time, so `resolved_customer_id` is already cached
  //     and the token-only path resolves. Nothing is added for it, and
  //     `checkout_modal_loyalty_proof.test.js` keeps proving that.
  //
  // So the raw value is only ever SENT in a request body, only when the
  // customer just proved that exact destination seconds earlier, and
  // never written to storage — which is precisely the boundary
  // `load_verified_customer`'s own docstring draws. The backend re-proves
  // the token on every call regardless; nothing here is a trust decision
  // made in the browser.
  const loyaltyVerification = useMemo(() => {
    const fromThisModal = buildVerificationPayload(verifiedTokens);
    if (fromThisModal) return fromThisModal;
    if (!verifiedProof?.token) return null;
    const channel = verifiedProof.channel || "email";
    return {
      channel,
      checkout_attempt_token: verifiedProof.token,
      channels: { [channel]: { token: verifiedProof.token,
                               verified_at: verifiedProof.verifiedAt || null } },
      // The KEY is absent, not undefined, when the proof carries no
      // destination — so a restored proof produces an object of exactly
      // the shape it always did, and the existing contract test
      // (`checkout_modal_loyalty_proof.test.js`) needed no amendment to
      // keep proving that no raw phone or email reaches the panel.
      ...(verifiedProof.destinationValue
        ? { destination_value: verifiedProof.destinationValue }
        : {}),
    };
  }, [verifiedTokens, verifiedProof]);
  // Walk-in loyalty verification (Sep 2026) — the missing way IN.
  //
  // A Dine-In/Takeout customer is recognized from their phone number
  // without any OTP (the approved walk-in convenience), so they arrive
  // here recognized but with no proof — and loyalty needs one, because
  // points are money and a typed phone number is not identity. Until
  // now checkout simply showed nothing, and the only OTP the storefront
  // offered in this mode was buried inside "View my past orders", which
  // no customer would think to use to spend points.
  //
  // So checkout offers it directly, and only to a customer we already
  // recognize: an unrecognized visitor sees nothing at all, which is
  // what keeps this from answering "is there a membership for this
  // number?" for anyone who asks.
  const [loyaltyVerifyOpen, setLoyaltyVerifyOpen] = useState(false);
  const loyaltyVerifyNeeded = !loyaltyVerification && !!memberRecognized;
  // Phone is the walk-in channel; email is the fallback when that is
  // all we have. Same two channels `getChannelMeta` already knows.
  const loyaltyVerifyChannel = isValidPhone(customer?.phone) ? "sms" : "email";
  const loyaltyValue = useMemo(
    () => (loyaltySummary && loyaltyPoints > 0
      ? Math.floor(loyaltyPoints / (loyaltySummary.redeem_points || 1)) *
        (loyaltySummary.redeem_value_cents || 0) / 100
      : 0),
    [loyaltyPoints, loyaltySummary],
  );
  const amountDue = Math.max(0, total - loyaltyValue);
  // Loyalty redemption at the reader (Sep 2026) — SUPPORTED on every
  // payment method this checkout offers.
  //
  // This was `paymentMethod !== "stripe_terminal"` while the reader
  // charged `order.total` and carried none of the reservation
  // claim/unclaim machinery: offering the control would have let a
  // customer select points, see a discount, and be charged the full
  // amount. `payments/stripe_terminal/service.py::initiate_session` now
  // claims the reservation before the PaymentIntent exists and charges
  // `external_payable_cents`, so the reduction the customer is shown is
  // the amount the reader asks for.
  //
  // Card is the primary kiosk method, so gating redemption on cash would
  // have meant most members could not spend their points at all.
  //
  // The panel is still offered only to a VERIFIED member — that gate is
  // `loyaltyVerifyNeeded` / `loyaltyVerification` below and is unchanged.
  // The backend remains the authority on both the money and the points:
  // nothing this component computes reaches the charge.
  // Defence in depth for proof retraction. `LoyaltyRedeemPanel` already
  // reports `onSummary(null)`/`onChange(0)` when its proof disappears,
  // but the panel can also be unmounted by the branch above before that
  // callback ever runs. Anything derived from a proof that no longer
  // exists must not survive here either.
  useEffect(() => {
    if (!loyaltyVerification) {
      setLoyaltySummary(null);
      setLoyaltyPoints(0);
    }
  }, [loyaltyVerification]);
  // Parcel Shipping Checkout Enforcement — a shipping order must never
  // reach payment without a currently-selected carrier quote. Without
  // this, "Place Order" was reachable with $0 shipping charged and no
  // service on file (the exact production defect this fix closes).
  // Mirrored server-side by the `shipping_method_required` gate in
  // `routes/payments.py::create_session` — the frontend disables the
  // button as a UX courtesy, the backend is the actual authority.
  const shippingMethodMissing = orderType === "shipping" && !shippingSelection?.quote_id;

  // Fire once per transition into "blocked" — a proactive, informational
  // counterpart to the reactive `shipping_checkout_blocked` (which only
  // fires on an actual submit attempt).
  const shippingSelectionRequiredLoggedRef = useRef(false);
  useEffect(() => {
    if (shippingMethodMissing) {
      if (!shippingSelectionRequiredLoggedRef.current) {
        shippingSelectionRequiredLoggedRef.current = true;
        logCustomerAuditEvent(slug, { event: "shipping_selection_required", orderType, catalogId: catalog?.id, sessionId, customerId: customer?.id });
      }
    } else {
      shippingSelectionRequiredLoggedRef.current = false;
    }
  }, [shippingMethodMissing, slug, orderType, catalog?.id, sessionId, customer?.id]);

  // Rudras-AI V1.0 Commerce Engine (Mar 2026, hardened Mar 2026 post
  // production Stripe smoke) — payment session. Extracted out of
  // `submit()` (Local Delivery Quote/Fee Checkout Enforcement) so the
  // Local Delivery "Accept & Pay" step can resume payment for an
  // already-created draft order without re-running checkout, using the
  // exact same call this component always used for every other card
  // payment. `localDeliverySelection` is `{quote_id}` when called after
  // an accepted Local Delivery quote; omitted for every other order.
  // `acceptedDeliveryTotal` (Tenant-Review Checkout Startup Failure
  // Correction, Aug 2026) — the exact quote-inclusive total the
  // customer already confirmed via "Accept & Pay", captured by
  // `confirmLocalDeliveryQuoteAndPay` BEFORE it clears
  // `localDeliveryQuote` from state. Only used to redisplay that
  // already-confirmed amount on a payment-start-failure retry screen —
  // never sent to the backend, never re-derived client-side.
  const startCardPayment = async (order, checkoutPayload, localDeliverySelection, acceptedDeliveryTotal) => {
    // Defensive: the backend accepts either `id` OR `order_code`
    // in the payment session route (see payments.py::_resolve_order
    // — `$or: [{id}, {order_code}]`). If the checkout response ever
    // omits `id` we fall back to `order_code` so we never fire a
    // payment session with an undefined identifier.
    const orderIdentifier = order?.id || order?.order_code;
    if (!orderIdentifier) {
      console.error("[checkout] card path: order response missing id + order_code", order);
      toast.error(
        "We placed your order but couldn't start the card payment. "
        + paymentStartFailureSuffix()
      );
      return; // keep the customer on the checkout screen; DO NOT flash Order Confirmed
    }
    try {
      const paymentPayload = {
        order_id: orderIdentifier,
        method: "card",
        origin_url: window.location.origin,
        customer: checkoutPayload.customer,
        // Parcel Shipping Checkout Enforcement — carries the entry
        // catalog-specific linkSlug through the Stripe redirect so
        // cancellation/return navigates back to the SAME storefront
        // instead of the tenant's bare default catalog.
        link_slug: loadLinkSlug(slug) || undefined,
      };
      // CP2.4 — attach the customer's persisted shipping quote so the
      // backend can recompute pricing server-authoritatively, persist
      // `retail_orders.shipping`, and tag Stripe with a
      // `has_shipping_line_item` metadata flag.
      if (orderType === "shipping" && shippingSelection?.quote_id) {
        paymentPayload.shipping = {
          quote_id: shippingSelection.quote_id,
          service_code: shippingSelection.service_code,
        };
      }
      // Local Delivery Quote/Fee Checkout Enforcement — attach the
      // customer's accepted Local Delivery quote so the backend can
      // recompute the delivery fee server-authoritatively (see
      // `finalize_local_delivery_draft`) before the Stripe session is
      // created. Absent for every order that isn't Local Delivery with
      // Uber Direct active — the backend gate only requires this when
      // it's actually applicable, so omitting it is safe everywhere
      // else, exactly like `shipping` above.
      if (orderType === "delivery" && localDeliverySelection?.quote_id) {
        paymentPayload.local_delivery = { quote_id: localDeliverySelection.quote_id };
      }
      const { data: sess } = await axios.post(
        `${RETAIL_BASE}/${slug}/payments/session`,
        paymentPayload,
        // Security correction (Aug 2026) — the just-received checkout
        // access token (stored a few lines above, same order_code).
        { headers: { "X-Order-Access": loadOrderAccessToken(slug, order?.order_code) } },
      );
      if (sess?.checkout_url) {
        // Stripe Production Cutover (Feb 2026) — signal to the parent
        // page's `beforeunload` guard that this navigation is
        // intentional so the browser doesn't show a "Leave site?"
        // prompt on top of Stripe's redirect.
        try { window.__rudrasAllowUnload = true; } catch (_e) { /* noop */ }
        window.location.href = sess.checkout_url;
        return; // halt — Stripe will redirect us back on success.
      }
      // 200 without a checkout_url shouldn't happen — treat as failure.
      console.error("[checkout] payments/session returned no checkout_url", sess);
      toast.error(
        "We placed your order but the payment page didn't open. "
        + paymentStartFailureSuffix(order?.order_code || orderIdentifier)
      );
      return;
    } catch (payErr) {
      const det = payErr?.response?.data?.detail;
      const code = det && typeof det === "object" && det.code;
      // False-address-drift-rejection UI fix (Aug 2026) — these codes
      // mean the ACCEPTED Local Delivery quote this payment attempt
      // carried is no longer usable (address changed since acceptance,
      // quote expired, or an order/quote mismatch). By this point
      // `confirmLocalDeliveryQuoteAndPay` has already cleared
      // `localDeliveryQuote`, so falling through to the default Order
      // Summary view here would show a merchandise-only total next to
      // a fully actionable "Place Order" button — the exact defect
      // this fix closes. Route to an explicit blocked state instead.
      if (orderType === "delivery" && localDeliverySelection?.quote_id
          && LOCAL_DELIVERY_QUOTE_INVALIDATED_CODES.has(code)) {
        setPendingLocalDeliveryOrder(order);
        setPendingLocalDeliveryPayload(checkoutPayload);
        setLocalDeliveryNeedsNewQuote(true);
        const msg = (det && typeof det === "object" && det.message)
          || "Your delivery pricing needs to be refreshed. Please request a new delivery quote.";
        toast.error(msg);
        return;
      }
      // Tenant-Review Checkout Startup Failure Correction (Aug 2026) —
      // this is a GENERIC payment-start failure (not one of the
      // quote-invalidation codes handled above). For a Local Delivery
      // order that had already reached this point via an explicit
      // "Accept & Pay" click, route to the dedicated retry state
      // (preserves the order + accepted quote) instead of falling
      // through to the misleading pre-quote Order Summary view.
      if (orderType === "delivery" && localDeliverySelection?.quote_id) {
        setDeliveryPaymentFailed({
          order, checkoutPayload,
          quoteId: localDeliverySelection.quote_id,
          total: acceptedDeliveryTotal,
          // Section 1 (PR #116 quote-retry correction) — the delivery-
          // fee component alone, isolated from merchandise/tax/tip, so
          // `retryDeliveryPayment` can tell a genuinely changed fee
          // apart from an unchanged one after revalidating the quote.
          deliveryFee: Number.isFinite(acceptedDeliveryTotal)
            ? acceptedDeliveryTotal - subtotal - tax - tip
            : undefined,
          orderCode: order?.order_code || orderIdentifier,
        });
        const msg = (det && typeof det === "object" && det.message)
          || (typeof det === "string" && det)
          || "We couldn't start the card payment. No charge was made — you can retry the same order below.";
        toast.error(msg);
        return;
      }
      const msg = (det && typeof det === "object" && det.message)
        || (typeof det === "string" && det)
        || `We placed your order but couldn't start the card payment. ${paymentStartFailureSuffix(order?.order_code || orderIdentifier)}`;
      toast.error(msg);
      return; // CRITICAL: no onPlaced() — customer must NOT see "Order Confirmed" on Stripe failure
    }
  };

  // Pre-Order Total-Disclosure Correction (Aug 2026) — turns the
  // checkout draft created above into a real, operational order.
  // Re-POSTs `/checkout` with the EXACT SAME `checkoutPayload` (same
  // `session_id`/cart) but `draft: false` — the backend's own cart-
  // based idempotency then PROMOTES the existing draft row in place
  // (allocates the real order_code, runs customer-lifecycle/coupon/
  // notification effects exactly once) rather than creating a second
  // order. Safe to call more than once for the same draft: a second
  // call is a no-op that returns the SAME already-promoted order.
  const promoteCheckoutDraft = async (checkoutPayload) => {
    const { data } = await axios.post(
      `${RETAIL_BASE}/${slug}/checkout`,
      { ...checkoutPayload, draft: false },
    );
    // Security correction (Aug 2026) — this is where a Local Delivery
    // draft first becomes a real, customer-facing order (real
    // order_code), so it's also where its one-time public order-access
    // credential first exists. Stored client-side ONLY (sessionStorage,
    // never the URL) — see `retailSessionKeys.js`'s own docstring.
    if (data.access_token && data.order?.order_code) {
      saveOrderAccessToken(slug, data.order.order_code, data.access_token);
    }
    // The order carries its own server-computed totals; nothing from the
    // browser is stamped onto it (Order Discount, Sep 2026).
    return data.order;
  };

  // Local Delivery Quote/Fee Checkout Enforcement — requests a quote for
  // the just-created checkout draft. Three outcomes:
  //   * Quote obtained → stored in state, `submit()` halts here, and the
  //     modal shows the fee-confirmation view; the customer's explicit
  //     "Accept & Pay" click (see `confirmLocalDeliveryQuoteAndPay`
  //     below) is what actually continues to payment. Returns `false`
  //     ("do not fall through to a plain card-payment call").
  //   * `local_delivery_disabled` — this tenant isn't using Local
  //     Delivery/Uber Direct at all (the legacy flat-fee delivery
  //     charge applies instead, computed client-side same as always).
  //     Returns `true` ("proceed to payment with no quote attached" —
  //     matches the backend gate's own narrow scope).
  //   * Any other failure — customer-safe message shown, checkout halts.
  //     Returns `false`.
  const requestLocalDeliveryQuote = async (order, checkoutPayload) => {
    setLocalDeliveryQuoteBusy(true);
    try {
      const { data: quote } = await axios.post(
        `${RETAIL_BASE}/${slug}/local-delivery/quote`,
        { order_id: order?.id || order?.order_code, catalog_id: catalog?.id },
      );
      logCustomerAuditEvent(slug, { event: "local_delivery_quote_received", orderType, catalogId: catalog?.id, sessionId, customerId: customer?.id });
      setLocalDeliveryQuote(quote);
      setPendingLocalDeliveryOrder(order);
      setPendingLocalDeliveryPayload(checkoutPayload);
      return false;
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "local_delivery_disabled") {
        return true;
      }
      // Catalog Fulfillment PR 4 (Aug 2026) — an Own Delivery catalog's
      // quote failure (Google Routes unavailable/timed out/misconfigured,
      // or no pricing rule matches) must ALWAYS fail closed with this
      // exact customer-safe wording, regardless of the backend's own
      // (Uber-oriented) error message — never a fee, never a fallback,
      // never a distinguishing internal reason. Every other catalog's
      // wording (Uber quote failures, the disabled-tenant case above)
      // is unchanged.
      const msg = catalog?.local_delivery_provider === "own"
        ? "Delivery pricing is temporarily unavailable — please try again."
        : ((err?.response?.data?.detail?.message)
          || "We couldn't get a delivery quote right now. Please try again.");
      toast.error(msg);
      logCustomerAuditEvent(slug, { event: "local_delivery_quote_failed", orderType, reasonCode: code || "unknown", catalogId: catalog?.id, sessionId, customerId: customer?.id });
      return false;
    } finally {
      setLocalDeliveryQuoteBusy(false);
    }
  };

  // Local Delivery Quote/Fee Checkout Enforcement — the customer's
  // explicit acceptance of the fee shown in the confirmation view.
  // Only after this succeeds does payment actually start; a failure
  // here (expired/tampered/foreign quote) clears the stored quote so
  // the customer must request a fresh one, never silently retries with
  // stale state.
  const confirmLocalDeliveryQuoteAndPay = async () => {
    if (localDeliveryQuoteBusy || !localDeliveryQuote || !pendingLocalDeliveryOrder) return;
    // Proactive quote-expiry check (Aug 2026 correction) — the server
    // is the authoritative gate (`finalize_local_delivery_draft` always
    // rejects an expired accepted quote before Stripe is ever called),
    // but blocking here too means the customer never even attempts a
    // doomed acceptance; they're routed straight to a fresh quote.
    if (localDeliveryQuote.expires_at && Date.now() > new Date(localDeliveryQuote.expires_at).getTime()) {
      toast.error("This delivery quote has expired. Requesting a new one.");
      await requestFreshLocalDeliveryQuote();
      return;
    }
    // Gate C5A — the customer's own 3:00 decision window. Same
    // relationship to the server as the check above: this only spares
    // the customer a doomed round-trip. `service.accept_quote` re-reads
    // the stored `customer_decision_deadline` and refuses regardless of
    // what this client believed, so a tampered clock changes nothing.
    //
    // Deliberately does NOT auto-refresh: Gate C5A §5.3 forbids an
    // endless automatic three-minute re-quote loop, so an elapsed window
    // shows the recovery panel and waits for an explicit
    // "Refresh delivery fee" click.
    if (secondsUntil(localDeliveryQuote.customer_decision_deadline, Date.now()) === 0) {
      toast.error("Delivery quote expired. Refresh the delivery fee to continue.");
      setLocalDeliveryQuote(null);
      setLocalDeliveryNeedsNewQuote(true);
      return;
    }
    // Capture the exact total the customer is about to confirm BEFORE
    // any state is cleared — this is the number a payment-start-failure
    // retry screen must redisplay (see `startCardPayment`'s generic
    // catch branch), since `localDeliveryQuote` is nulled below.
    const acceptedTotal = subtotal + tax + Number(localDeliveryQuote.fee_amount || 0) + tip;
    setLocalDeliveryQuoteBusy(true);
    try {
      await axios.post(
        `${RETAIL_BASE}/${slug}/local-delivery/quotes/${localDeliveryQuote.quote_id}/accept`,
      );
      logCustomerAuditEvent(slug, { event: "local_delivery_quote_accepted", orderType, catalogId: catalog?.id, sessionId, customerId: customer?.id });
      const checkoutPayload = pendingLocalDeliveryPayload;
      const quoteId = localDeliveryQuote.quote_id;
      // Pre-Order Total-Disclosure Correction (Aug 2026) — THIS is the
      // customer's final confirmation of the delivery-inclusive total.
      // Only now does the checkout draft become a real, operational
      // order (real order_code, customer-lifecycle effects, visible in
      // Retail Orders) — never earlier.
      const order = await promoteCheckoutDraft(checkoutPayload);
      logCustomerAuditEvent(slug, { event: "local_delivery_order_confirmed", orderType, catalogId: catalog?.id, sessionId, customerId: customer?.id });
      setLocalDeliveryQuote(null);
      setPendingLocalDeliveryOrder(null);
      setPendingLocalDeliveryPayload(null);
      await startCardPayment(order, checkoutPayload, { quote_id: quoteId }, acceptedTotal);
    } catch (err) {
      const det = err?.response?.data?.detail;
      const msg = (det && typeof det === "object" && det.message)
        || "This delivery quote is no longer valid. Please request a new one.";
      toast.error(msg);
      logCustomerAuditEvent(slug, { event: "local_delivery_quote_accept_failed", orderType, reasonCode: (det && det.code) || "unknown", catalogId: catalog?.id, sessionId, customerId: customer?.id });
      // Reset so the customer can request a fresh quote rather than
      // retrying against one the backend already rejected.
      setLocalDeliveryQuote(null);
      setPendingLocalDeliveryOrder(null);
      setPendingLocalDeliveryPayload(null);
    } finally {
      setLocalDeliveryQuoteBusy(false);
    }
  };

  const cancelLocalDeliveryQuote = () => {
    setLocalDeliveryQuote(null);
    setPendingLocalDeliveryOrder(null);
    setPendingLocalDeliveryPayload(null);
    setLocalDeliveryNeedsNewQuote(false);
  };

  // False-address-drift-rejection UI fix (Aug 2026) — the customer's
  // explicit exit from the "delivery pricing needs to be refreshed"
  // blocked state (see the render block below `if (localDeliveryQuote)`).
  // Discards the invalidated quote_id entirely and requests a genuinely
  // fresh one — never resubmits the rejected quote_id.
  const requestFreshLocalDeliveryQuote = async () => {
    if (localDeliveryQuoteBusy || !pendingLocalDeliveryOrder) return;
    const order = pendingLocalDeliveryOrder;
    const checkoutPayload = pendingLocalDeliveryPayload;
    setLocalDeliveryNeedsNewQuote(false);
    setPendingLocalDeliveryOrder(null);
    setPendingLocalDeliveryPayload(null);
    await requestLocalDeliveryQuote(order, checkoutPayload);
  };

  const cancelLocalDeliveryNeedsNewQuote = () => {
    setLocalDeliveryNeedsNewQuote(false);
    setPendingLocalDeliveryOrder(null);
    setPendingLocalDeliveryPayload(null);
  };

  // Tenant-Review Checkout Startup Failure Correction (Aug 2026),
  // extended by the PR #116 quote-retry correction (Section 1) — the
  // customer's explicit retry after a payment-start failure (see
  // `deliveryPaymentFailed` state above). Reuses the SAME already-
  // created (already-promoted, never-a-draft) order — never re-runs
  // `/checkout`, so a retry can never create a duplicate order.
  //
  // Never trusts that the previously-accepted quote is still usable:
  // every retry first revalidates it against the SAME server-
  // authoritative endpoint the original quote came from
  // (`quote_service.request_quote`, via `POST .../local-delivery/quote`).
  // That endpoint's own pre-existing idempotent-reuse design (PR #87)
  // hands back the SAME accepted quote (byte-identical fee) if it's
  // still valid, or transparently retires it and mints a genuinely
  // fresh one if it expired while the customer sat on the failed
  // Stripe redirect — either way, the fee returned here is always the
  // CURRENT, real, still-valid one; the client never guesses.
  //   * fee unchanged (same quote reused, or a fresh quote happens to
  //     match) → accept the new quote_id if it differs, then continue
  //     straight to Stripe with the SAME already-confirmed total — no
  //     second confirmation screen, matching the "retry only Stripe
  //     session creation" requirement.
  //   * fee changed → NEVER silently charge the new amount. Route to
  //     the existing fee-confirmation screen (`localDeliveryQuote`)
  //     with the fresh quote, so "Place Order & Continue to Payment ·
  //     $NEW_TOTAL" is the customer's own explicit reconfirmation
  //     click — only after that does Stripe ever see the updated
  //     amount.
  //   * refresh itself fails (delivery disabled, provider error, etc.)
  //     → block with an actionable error; the stale total is never
  //     resubmitted to Stripe.
  // Guarded against duplicate/concurrent clicks by `deliveryPaymentRetryBusy`.
  const retryDeliveryPayment = async () => {
    if (deliveryPaymentRetryBusy || !deliveryPaymentFailed) return;
    const { order, checkoutPayload, quoteId, total, deliveryFee } = deliveryPaymentFailed;
    setDeliveryPaymentRetryBusy(true);
    try {
      const { data: quote } = await axios.post(
        `${RETAIL_BASE}/${slug}/local-delivery/quote`,
        { order_id: order?.id || order?.order_code, catalog_id: catalog?.id },
      );
      const sameQuote = quote.quote_id === quoteId;
      const feeUnchanged = Number.isFinite(deliveryFee)
        && Math.abs(Number(quote.fee_amount || 0) - deliveryFee) < 0.005;
      if (sameQuote || feeUnchanged) {
        if (!sameQuote) {
          // A genuinely new quote with a matching fee still needs its
          // own explicit acceptance before `finalize_local_delivery_
          // draft` will honor it — the OLD accepted quote is gone.
          await axios.post(
            `${RETAIL_BASE}/${slug}/local-delivery/quotes/${quote.quote_id}/accept`,
          );
        }
        setDeliveryPaymentFailed(null);
        await startCardPayment(order, checkoutPayload, { quote_id: quote.quote_id }, total);
      } else {
        // Fee changed — require the customer's explicit reconfirmation
        // via the same screen the original quote used. Never reuses
        // the stale `total`; the confirmation screen computes it fresh
        // from `quote.fee_amount`.
        setDeliveryPaymentFailed(null);
        setLocalDeliveryQuote(quote);
        setPendingLocalDeliveryOrder(order);
        setPendingLocalDeliveryPayload(checkoutPayload);
        toast.error("Your delivery fee has changed since your last attempt. Please confirm the new total.");
      }
    } catch (err) {
      const det = err?.response?.data?.detail;
      const code = det && typeof det === "object" && det.code;
      const msg = (det && typeof det === "object" && det.message)
        || (code === "local_delivery_disabled"
          ? "Delivery pricing isn't available right now. Please try again shortly."
          : "We couldn't refresh your delivery pricing. No charge was made — you can retry below.");
      toast.error(msg);
      // Leave the customer on the SAME retry screen (never silently
      // fall through to a stale-quote Stripe call) — restore state so
      // "Retry Payment" remains available.
      setDeliveryPaymentFailed({ order, checkoutPayload, quoteId, total, deliveryFee, orderCode: order?.order_code || order?.id });
    } finally {
      setDeliveryPaymentRetryBusy(false);
    }
  };

  const cancelDeliveryPaymentFailed = () => {
    setDeliveryPaymentFailed(null);
  };

  // Keep the ref pointed at the latest closure of `submit` — used by the
  // post-verification effect above without adding `submit` to its deps.
  const submit = async () => {
    if (submitting) return;
    // Parcel Shipping Checkout Enforcement — never let a shipping order
    // reach payment without a selected quote, even if a caller somehow
    // fires `submit()` directly (e.g. Enter key on a focused element)
    // bypassing the disabled button.
    if (shippingMethodMissing) {
      toast.error("Select a shipping method before continuing.");
      logCustomerAuditEvent(slug, { event: "shipping_checkout_blocked", orderType, reasonCode: "no_selection", catalogId: catalog?.id, sessionId, customerId: customer?.id });
      return;
    }
    // Same shape as the guard above: the button is disabled, but a direct
    // `submit()` (Enter on a focused field) must not bypass it. The
    // backend refuses this order too — this only saves the round trip and
    // gives a clearer message.
    if (scheduledWindowMissing) {
      toast.error("Choose a pickup time to continue.");
      return;
    }
    if (fulfillmentBlocked) {
      toast.error(
        fulfillmentEarliestLabel
          ? `Not available for immediate fulfillment. Earliest: ${fulfillmentEarliestLabel}.`
          : "Not available for immediate fulfillment right now.",
      );
      return;
    }
    // Sprint A — block submit if the required channels aren't verified.
    // For pickup/delivery/takeout this means "email verified". For
    // delivery the backend additionally requires a valid phone format.
    // Sprint 1 Go-Live (Feb 2026) — Cash payments SKIP verification
    // entirely (matches the backend's `skip_verification_for_cash`
    // gate). Cash customers hand cash to staff at the counter; the
    // OTP step adds nothing and blocked the Dine-in "Pay at Counter"
    // flow. Card payments still gate on verification.
    if (paymentMethod !== "cash" && !isFullyVerified) {
      // Pre-flight email format check so we don't open the modal with
      // a value we know will be rejected by the backend.
      const email = (customer?.email || "").trim();
      if (requiredChannels.includes("email") && !isValidEmail(email)) {
        toast.error("Please add a valid email address to your details first.");
        return;
      }
      // Phone/OTP validation correction (Aug 2026) — confirmed staging
      // defect: `requiredChannels` gains an "sms" requirement whenever
      // the tenant's own `delivery_phone_verification_required` config
      // says so (see the `requiredChannels` memo above), regardless of
      // payment method — but only the narrower, LEGACY check below
      // (scoped to `paymentMethod !== "card"`) ever validated the phone
      // before opening `VerificationModal`, which performs no
      // validation of its own. A card-paying Local Delivery customer
      // with an invalid phone could reach the OTP modal untouched.
      // Mirrors the email check immediately above.
      if (requiredChannels.includes("sms") && !isValidPhone(customer?.phone)) {
        toast.error("Please add a valid phone number to your details first.");
        return;
      }
      // Go-Live Sprint (Feb 2026, item #7 correction) — the delivery
      // phone-format check is now tenant-driven. Only enforce a
      // client-side digit-count check when the delivery mode's
      // `require_phone` rule is set AND payment is not card. This
      // keeps parity with the backend rule and prevents checkout
      // from silently adding a phone requirement the tenant didn't
      // opt into.
      const modeRules = orderModes?.[orderType] || {};
      if (orderType === "delivery" && modeRules.require_phone && paymentMethod !== "card") {
        if (!isValidPhone(customer?.phone)) {
          toast.error("Please add a valid phone number for delivery.");
          return;
        }
      }
      setVerificationOpen(true);
      return;
    }
    setSubmitting(true);
    try {
      // Order Discount (Sep 2026) — never place an order ahead of the
      // server's pricing: wait for the in-flight Order Summary, and refuse
      // if the server could not price the cart.
      const pricedSummary = (!summaryPendingRef.current && latestSummaryRef.current)
        ? latestSummaryRef.current
        : await awaitOrderSummary();
      if (!pricedSummary) {
        toast.error("We couldn't calculate your order total. Please try again.");
        return;
      }
      const phone = customer?.phone || "";
      const email = customer?.email || "";
      const name = customer?.name || "Guest";
      // Iter55 — A2P 10DLC informational/transactional SMS consent.
      // Forwarded to the backend so the order's customer block carries
      // a verifiable consent trail before any Twilio SMS is dispatched.
      const smsConsentAgreed = !!customer?.smsConsent && !!phone;
      const smsConsentAt = smsConsentAgreed ? (customer?.smsConsentAt || new Date().toISOString()) : null;
      const verificationPayload = buildVerificationPayload(verifiedTokens);
      // P1.1 (Feb 2026 SMS Sprint) — full A2P 10DLC consent envelope.
      // WelcomeModal captures the browser-side signals (source URL +
      // text version). We omit `sms_consent_ip_hash` here because the
      // client can't compute a keyed hash safely; the backend already
      // has the option to derive one from the request headers if the
      // A2P audit ever calls for it.
      const consentEnvelope = smsConsentAgreed ? {
        sms_consent_given: true,
        sms_consent_timestamp: smsConsentAt,
        sms_consent_source_url: (customer?.smsConsentSourceUrl
          || (typeof window !== "undefined" ? window.location.href : "")).slice(0, 512),
        sms_consent_text_version: customer?.smsConsentTextVersion || "v1.0",
      } : {};
      const payload = {
        session_id: sessionId,
        customer: {
          name, phone, email,
          sms_consent_agreed: smsConsentAgreed,
          sms_consent_at: smsConsentAt,
          sms_consent_source: smsConsentAgreed ? "retail_welcome_modal" : null,
          ...consentEnvelope,
        },
        fulfillment: {
          mode: orderType,
          // Catalog Fulfillment Scheduling Phase 2 — the backend's own
          // canonical slot instant, echoed back verbatim. Absent for an
          // ASAP order, which keeps ASAP a semantic request rather than a
          // frozen timestamp. The backend revalidates this against the
          // live schedule before any money moves and stores only its OWN
          // resolved window.
          scheduled_at: scheduledSelection?.start || null,
          notes: Number(pricedSummary.tip) > 0 ? `Tip: ${formatMoney(pricedSummary.tip, currency)}` : "",
          table_number: orderType === "dine_in" ? (customer?.tableNumber || "") : "",
          // Iter45 / Step 2 — delivery_address may now be the structured
          // object the WelcomeModal builds, or empty for non-delivery.
          // The backend accepts both shapes (dict + legacy string) for
          // back-compat; we just pass whatever WelcomeModal stored.
          // Hospitality H1 — a hotel-referred order sends NO address: the
          // server replaces it with the hotel's stored one regardless.
          delivery_address: (orderType === "delivery" && !hostDestination) ? (customer?.deliveryAddress || null) : null,
          // Phase 3A.5 — set only when the tenant has address validation
          // enabled AND the customer completed
          // `LocalDeliveryAddressValidationPanel` (which stores the
          // confirmed token on `customer.addressValidationToken`, same
          // pattern as `deliveryAddress` above). Omitted/null is fully
          // backward compatible — the backend gate only activates per
          // tenant flag, never based on this field's mere presence.
          address_validation: (orderType === "delivery" && !hostDestination && customer?.addressValidationToken)
            ? { checkout_attempt_token: customer.addressValidationToken }
            : null,
        },
        // `stripe_terminal` is the RAIL, not a customer-visible payment
        // method: the order is paid by card, and which reader took the
        // card is recorded on the `payment_transactions` row
        // (`provider=stripe_terminal`), exactly as Hosted Checkout
        // records `provider=stripe` against an order whose
        // `payment_method` is also plain "card".
        //
        // This also matters at the API boundary:
        // `retail/orders.py::_validate_public_checkout_payment_method`
        // accepts ONLY {cash, card} from the unauthenticated storefront
        // (beta-launch audit F-1), so sending the rail name here would
        // be rejected outright with `payment_method_invalid` before the
        // order is ever created.
        payment_method: paymentMethod === "stripe_terminal" ? "card" : paymentMethod,
        // Stripe Production Cutover fix (Feb 2026) — customer-authorised
        // tip. The backend adds this to `order.total` so Stripe (or the
        // cash drawer) actually collects the gratuity. Before this the
        // frontend showed a tip on the summary but silently discarded
        // it server-side.
        //
        // Order Discount (Sep 2026) — a percentage tip is sent as the
        // PERCENTAGE (`tip_percent`) and the server computes the amount on
        // post-discount merchandise; only a custom tip is sent as an amount.
        ...tipRequest,
        // Sprint A — channel-agnostic verification payload. Today only
        // `email` is populated; SMS / multi-channel drop in without
        // touching this call site.
        verification: verificationPayload,
        // Loyalty Phase 1 (Sep 2026) — a PROPOSAL. `retail/orders.py::
        // _resolve_order_loyalty` re-derives the member's balance and
        // every ceiling server-side before a single point is reserved.
        loyalty_points: loyaltyPoints > 0 ? loyaltyPoints : undefined,
        // Sep 2026 correction — the proof that says WHOSE points these
        // are, sent as its own field.
        //
        // `verification` above is the order-submission gate and is
        // deliberately left alone: a Cash order has none (checkout skips
        // email OTP for cash) while the member is nonetheless fully
        // verified from the welcome step, which is exactly the pilot's
        // flow. Without a separate field, redemption on cash would be
        // impossible — or the submission gate would have to be weakened
        // to allow it. Neither is acceptable, so they stay separate.
        //
        // Only the two fields `VerificationIn` declares: a channel name
        // and the server-issued token. No phone, no email, no customer
        // id, no company id. The server re-validates the token against
        // THIS tenant and catalog and re-derives the member from it, so
        // nothing here is a claim the browser gets to make.
        loyalty_verification: (loyaltyPoints > 0 && loyaltyVerification)
          ? {
              channel: loyaltyVerification.channel,
              checkout_attempt_token: loyaltyVerification.checkout_attempt_token,
            }
          : undefined,
        // Pre-Order Total-Disclosure Correction (Aug 2026) — a Local
        // Delivery card order must not become an operational, customer-
        // facing order (real order_code, visible in Retail Orders,
        // customer-lifecycle effects) until the customer has actually
        // seen and confirmed the delivery-inclusive final total. This
        // first call only ever creates a `checkout_draft` — see
        // `promoteCheckoutDraft` below for the finalize call that turns
        // it into a real order. Every other order type is unaffected:
        // `draft` is simply absent (defaults to `false` server-side),
        // producing the exact same single-step order as always.
        ...(orderType === "delivery" && paymentMethod === "card" ? { draft: true } : {}),
        // Marketing P0-4 — `client_mode: "kiosk"` in `?kiosk=1` tablet
        // mode, otherwise nothing. A reporting hint only: it changes no
        // price, payment, OTP, fulfillment or ordering_channel.
        ...clientModeField(slug),
      };
      // Go-Live Sprint (Feb 2026, item #7 correction) — the placeholder
      // phone "000-000-0000" that used to be injected here for
      // "no-contact-required" modes matched the backend's fake-phone
      // detector (`_REPEATED_CHAR_RE`) and produced the misleading
      // "Please enter a real phone number." error on Dine-In + Cash
      // even when the customer had legitimately left the phone blank.
      // The backend now accepts a truly-blank phone (see
      // `retail/orders.py::_validate_customer`) so no placeholder is
      // needed — and any placeholder here would ACTIVELY break the
      // flow. Do NOT reintroduce.
      const { data } = await axios.post(`${RETAIL_BASE}/${slug}/checkout`, payload);
      // Security correction (Aug 2026) — the one-time per-order access
      // credential (absent for a `draft: true` call above, which has
      // no customer-facing order_code yet to attach it to). Stored
      // client-side ONLY, never in the URL — see
      // `retailSessionKeys.js`'s own docstring for the full rationale.
      if (data.access_token && data.order?.order_code) {
        saveOrderAccessToken(slug, data.order.order_code, data.access_token);
      }
      // The order carries its own server-computed totals; nothing from the
      // browser is stamped onto it (Order Discount, Sep 2026).
      const order = data.order;

      // Rudras-AI V1.0 Commerce Engine (Mar 2026, hardened Mar 2026 post
      // production Stripe smoke) — payment session.
      //
      // For CARD payments we route through the provider-aware payment
      // engine (`POST /retail/public/{slug}/payments/session`), which
      // returns a Stripe Checkout URL. On success we redirect the
      // customer to Stripe Hosted Checkout; on any failure we
      // deliberately DO NOT fall through to `onPlaced(order)` — that
      // would flash "Order Confirmed" even though no money moved.
      // The order stays in the DB (payment_status="pending") so the
      // operator can chase it up manually.
      //
      // For CASH we mark the payment on the engine (best-effort) and
      // then confirm — cash orders are considered paid on creation.
      if (paymentMethod === "card") {
        // Defensive: the backend accepts either `id` OR `order_code`
        // in the payment session route (see payments.py::_resolve_order
        // — `$or: [{id}, {order_code}]`). If the checkout response ever
        // omits `id` we fall back to `order_code` so we never fire a
        // payment session with an undefined identifier.
        const orderIdentifier = order?.id || order?.order_code;
        if (!orderIdentifier) {
          console.error("[checkout] card path: order response missing id + order_code", data);
          toast.error(
            "We placed your order but couldn't start the card payment. "
            + paymentStartFailureSuffix()
          );
          return; // keep the customer on the checkout screen; DO NOT flash Order Confirmed
        }
        // Local Delivery Quote/Fee Checkout Enforcement — a Local
        // Delivery order must never reach payment without a selected,
        // accepted Uber Direct quote. Request one now that the draft
        // order exists (the quote endpoint requires `order_id`); if
        // this tenant isn't using Local Delivery/Uber Direct at all,
        // `requestLocalDeliveryQuote` returns `true` and we fall
        // through to payment exactly as before (legacy flat-fee
        // delivery, unchanged). Otherwise it stores the quote + this
        // draft order/payload in state and returns `false` — the
        // customer's explicit "Accept & Pay" click on the fee-
        // confirmation view (rendered below in the JSX) is what
        // resumes payment via `confirmLocalDeliveryQuoteAndPay`.
        let orderToCharge = order;
        if (orderType === "delivery") {
          const shouldProceedWithoutQuote = await requestLocalDeliveryQuote(order, payload);
          if (!shouldProceedWithoutQuote) {
            return; // halt — quote-confirmation view takes over from here
          }
          // Pre-Order Total-Disclosure Correction (Aug 2026) — this
          // tenant isn't using Local Delivery/Uber Direct at all (the
          // legacy flat-fee delivery charge, already fully reflected in
          // `total` before the customer ever clicked). There is no
          // second confirmation step in that case, so the checkout
          // draft this click created is promoted immediately — the
          // customer's ONE click is still their one and only
          // confirmation of a total they already saw in full.
          orderToCharge = await promoteCheckoutDraft(payload);
        }
        await startCardPayment(orderToCharge, payload);
        return;
      } else if (paymentMethod === "clover_terminal") {
        // Milestone 4A — Clover Station Duo. The Rudras order was
        // just created above; hand off to the CloverTerminalPaymentDialog
        // which owns the rest of the lifecycle (session initiation,
        // polling, terminal-state → confirmation dispatch). We
        // deliberately DO NOT call `onPlaced` here — the dialog
        // decides when the customer sees "Order Confirmed" based
        // on the backend's authoritative session status. Never
        // trust frontend state to prove payment.
        const orderIdForClover = order?.id || order?.order_code;
        if (!orderIdForClover) {
          toast.error(
            "We placed your order but couldn't start the terminal payment. "
            + "Please retry, or ask a staff member for help."
          );
          return;
        }
        setCloverActiveOrder(order);
        // Save recovery pointer BEFORE opening the dialog so a
        // browser crash between here and dialog mount still recovers.
        // The dialog will fetch the session_id from the backend and
        // write the enriched pointer.
        saveRecovery(orderIdForClover, null);
        setCloverDialogOpen(true);
        return;   // hand off — dialog will call onPlaced on paid
      } else if (paymentMethod === "stripe_terminal") {
        // Stripe Reader M2. The Rudras order was just created above;
        // hand off to StripeTerminalPaymentDialog, which owns the rest
        // of the lifecycle (session initiation, polling, terminal-state
        // dispatch). `onPlaced` is deliberately NOT called here — the
        // dialog decides when the customer sees "Order Confirmed",
        // driven by the backend's authoritative session status. A
        // frontend success message never proves payment and never
        // releases an order to the kitchen.
        const orderIdForTerminal = order?.id || order?.order_code;
        if (!orderIdForTerminal) {
          toast.error(
            "We placed your order but couldn't start the card payment. "
            + "Please retry, or ask a staff member for help."
          );
          return;
        }
        setStripeTerminalActiveOrder(order);
        // Persist the recovery pointer BEFORE opening the dialog so a
        // browser crash in between still recovers. The dialog enriches
        // it with the session_id once the backend returns one.
        saveStripeRecovery(orderIdForTerminal, null);
        setStripeTerminalDialogOpen(true);
        return;   // hand off — dialog will call onPlaced on paid
      } else if (paymentMethod === "cash") {
        // Go-Live Correction (Feb 2026, Iter66) — Cash Payment
        // Confirmation Workflow bug fix.
        //
        // When the backend has flagged the order as
        // `awaiting_staff_payment=true` (currently: cash + dine-in,
        // per `retail/orders.py::checkout`), the "Pay at Counter"
        // workflow OWNS the payment lifecycle. Staff records receipt
        // via `PATCH /orders/{id}/mark-cash-received` which is the
        // ONLY writer allowed to flip payment_status → paid for cash.
        //
        // Previously this branch ALWAYS called the cash payment
        // session (`POST /payments/session`, method="cash") which
        // synchronously created a payment_transactions row and
        // stamped the order as paid — silently defeating the entire
        // waiting screen + Cash Received button flow (production
        // regression reported 2026-07-12, ALI-ORD-000037/38/39).
        //
        // For cash orders that ARE flagged as awaiting, we now skip
        // the session call entirely; the order stays `pending` +
        // `awaiting=true` and the operator drives the Cash Received
        // gesture from the Retail Orders Admin drawer.
        //
        // For cash orders NOT flagged as awaiting (legacy pickup /
        // takeout cash pre-workflow-split), preserve the previous
        // auto-mark-paid behaviour so we don't break in-flight
        // deployments — same call site, same shape.
        if (!order?.awaiting_staff_payment) {
          try {
            await axios.post(
              `${RETAIL_BASE}/${slug}/payments/session`,
              {
                order_id: order?.id || order?.order_code,
                method: "cash",
                origin_url: window.location.origin,
                customer: payload.customer,
              },
              { headers: { "X-Order-Access": loadOrderAccessToken(slug, order?.order_code) } },
            );
          } catch (_e) {
            // The local order is still placed — operator can record
            // cash manually from admin if the engine is unreachable.
          }
        }
      }
      onPlaced(order);
    } catch (e) {
      // Commerce Cleanup Sprint (Feb 2026) — friendly handling of the
      // restaurant-status / serviceability gates that come back as HTTP
      // 409 from `retail/orders.py`. Backend payload shape:
      //   detail: { code: "tenant_closed" | "delivery_unavailable" |
      //                   "pickup_only" | ..., message: "We're closed…" }
      // Surface the server's message verbatim so the operator's status
      // text reaches the customer unchanged.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Catalog Fulfillment Scheduling Phase 2 — the customer's chosen
      // window went away between page load and Pay (a tenant closure, a
      // schedule edit, or simply time passing). Clear the stale choice and
      // re-fetch the real windows so they pick again from current truth.
      // Deliberately NOT moved to another slot automatically: a changed
      // fulfillment time must be an explicit customer decision.
      if (status === 409 && detail?.code === "scheduled_slot_unavailable") {
        // Clear the WINDOW, never the choice to schedule. `null` is this
        // component's ASAP sentinel, so setting it here silently moved
        // the customer from "deliver at 9:15" to "deliver now" — and the
        // next Get Delivery Total quoted, and would have charged, an
        // ASAP delivery they never asked for. Keeping the empty
        // scheduled shape leaves them on the picker with the windows
        // re-read, which is the explicit re-choice this path always
        // intended.
        setScheduledSelection({ start: "", end: "", label: "", date: "" });
        fulfillmentAvailability.refresh();
        toast.error(detail.message || "That time is no longer available. Please choose another.");
        return;
      }
      // Diagnostic fallback (Feb 2026) — Pydantic body validation errors
      // return `detail` as an ARRAY of `{loc, msg, type}` shapes. Without
      // this branch the customer would only ever see a generic "Could
      // not place your order" and we would have no signal in the toast
      // as to which field was rejected. We concatenate the first 2 field
      // errors so the customer sees something like: "sms_consent_source_url:
      // value is too long"; operators can grab the full detail from
      // DevTools → Network → checkout → Response.
      const asPydanticArray = Array.isArray(detail) ? detail : null;
      // Structured field-error envelope from `retail/delivery/validators.py`
      // (`{"error": "delivery_address_invalid", "fields": {...}}`) —
      // NOT a Pydantic array, NOT a `{code, message}` 409. Handle it
      // explicitly so the customer sees "State: Use the 2-letter US
      // state code" instead of the generic fallback.
      const asFieldErrors =
        detail && typeof detail === "object"
        && detail.fields && typeof detail.fields === "object"
          ? detail
          : null;
      if (status === 409 && detail && typeof detail === "object") {
        const code = detail.code || "";
        const msg = detail.message || "We can't accept this order right now.";
        toast.error(msg, {
          duration: 6000,
          id: `checkout-409-${code || "generic"}`,
        });
        // Bubble structured info up so RetailCustomer can dim the
        // checkout button + show an in-page banner if it wants to.
        try { window.dispatchEvent(new CustomEvent("checkout:rejected", { detail: { code, message: msg } })); } catch (_err) { /* ignore */ }
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else if (asFieldErrors) {
        const first = Object.entries(asFieldErrors.fields).slice(0, 2)
          .map(([f, m]) => `${f}: ${m}`).join(" · ");
        const kind = asFieldErrors.error || "validation_failed";
        toast.error(
          `${kind === "delivery_address_invalid" ? "Delivery address" : "Order"} — ${first}`,
          { duration: 9000 },
        );
      } else if (detail && typeof detail === "object" && detail.message) {
        toast.error(detail.message);
      } else if (asPydanticArray && asPydanticArray.length) {
        const summary = asPydanticArray
          .slice(0, 2)
          .map((err) => {
            const field = (err.loc || []).slice(-1)[0] || "field";
            return `${field}: ${err.msg || "invalid"}`;
          })
          .join(" · ");
        toast.error(`Order rejected — ${summary}`, { duration: 8000 });
      } else if (detail && typeof detail === "object" && detail.error) {
        // Catch-all for `{error: "code", ...}` envelopes that don't carry
        // a friendly `.message` — items_unavailable, mode_disabled,
        // cart_race_no_order, etc. Surfaces the code so a support agent
        // can pattern-match instead of hitting the generic fallback.
        // Prefer any extra hint the server included (reason / channel).
        const code = detail.error;
        const hint = detail.reason || detail.channel || "";
        toast.error(
          hint ? `Order rejected — ${code} (${hint})` : `Order rejected — ${code}`,
          { duration: 9000, id: `checkout-err-${code}` },
        );
      } else {
        toast.error(
          status
            ? `Order rejected (HTTP ${status}). Please screenshot this and share with support.`
            : "Could not place your order. Please try again.",
        );
      }
      // Always log full detail to console so operators can grab the
      // raw shape from DevTools if the toast summary isn't enough.
      console.error("[checkout] rejected", { status, detail });
    } finally {
      setSubmitting(false);
    }
  };

  // Keep `submitRef` pointed at the latest closure of `submit` so the
  // post-verification effect can invoke it without carrying `submit` in
  // its dependency array (which would otherwise recreate on every render).
  submitRef.current = submit;

  // Tenant-Review Checkout Startup Failure Correction (Aug 2026) — a
  // payment-start failure (e.g. a Stripe-side outage) occurred AFTER
  // the customer already explicitly accepted the delivery-inclusive
  // total. Show ONLY this explicit retry state — never the default
  // Order Summary view, which would both misrepresent the total (no
  // delivery-fee line for this order type) and, if "Place Order" were
  // clicked, create a duplicate order via a second `/checkout` call.
  // No money was charged; the SAME order + accepted quote is reused.
  if (deliveryPaymentFailed) {
    return (
      <StorefrontModal
        testId="retail-checkout-local-delivery-payment-failed"
        onCancel={onClose}
        title="Payment Didn't Go Through"
        description="No charge was made — you can safely retry"
        zIndexClassName="z-50"
        maxWidthClassName="max-w-md"
      >
        <div className="text-stone-900">
          <h2 className="font-serif text-2xl">Payment Didn&apos;t Go Through</h2>
          <div
            className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900"
            role="status" data-testid="local-delivery-payment-failed-notice"
          >
            We couldn&apos;t start the card payment. No charge was made — your
            order and accepted delivery pricing are still on file. You can
            safely retry the same order below.
          </div>
          {deliveryPaymentFailed.orderCode && (
            <div className="text-xs text-stone-500 mt-2" data-testid="local-delivery-payment-failed-order-reference">
              Order reference: <span className="font-mono">{deliveryPaymentFailed.orderCode}</span>
            </div>
          )}
          {Number.isFinite(deliveryPaymentFailed.total) && (
            <div className="mt-3 flex items-center justify-between border-t-2 border-orange-500 pt-2 text-sm">
              <span className="font-bold">TOTAL DUE</span>
              <span className="font-extrabold text-xl" data-testid="local-delivery-payment-failed-total">
                {formatMoney(deliveryPaymentFailed.total, currency)}
              </span>
            </div>
          )}
          <div className="mt-5 grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="local-delivery-payment-failed-cancel"
              disabled={deliveryPaymentRetryBusy}
              onClick={cancelDeliveryPaymentFailed}
              className="h-11 rounded-lg border border-stone-300 text-sm font-medium text-stone-700 hover:border-stone-400 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="local-delivery-payment-failed-retry"
              disabled={deliveryPaymentRetryBusy}
              onClick={retryDeliveryPayment}
              className="h-11 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-60"
            >
              {deliveryPaymentRetryBusy
                ? "Retrying…"
                : Number.isFinite(deliveryPaymentFailed.total)
                ? `Retry Payment · ${formatMoney(deliveryPaymentFailed.total, currency)}`
                : "Retry Payment"}
            </button>
          </div>
        </div>
      </StorefrontModal>
    );
  }

  // False-address-drift-rejection UI fix (Aug 2026) — an accepted
  // Local Delivery quote was rejected at payment-session time (address
  // changed since acceptance / quote expired / order mismatch). Show
  // ONLY this explicit blocked state — never the default Order Summary
  // view, which has no delivery-fee line at all for this order type
  // and would otherwise present a misleading merchandise-only total
  // next to a fully actionable "Place Order" button. No submit-capable
  // control exists in this view; the only way forward is a fresh quote.
  if (localDeliveryNeedsNewQuote) {
    return (
      <StorefrontModal
        testId="retail-checkout-local-delivery-needs-new-quote"
        onCancel={onClose}
        title="Delivery Pricing Unresolved"
        description="Your delivery pricing needs to be refreshed"
        zIndexClassName="z-50"
        maxWidthClassName="max-w-md"
      >
        <div className="text-stone-900">
          <h2 className="font-serif text-2xl">Delivery Pricing Unresolved</h2>
          <div
            className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            role="status" data-testid="local-delivery-needs-new-quote-notice"
          >
            {/* Gate C5A §5.3 — one panel, two reasons, and the copy has
                to be true for both: the customer's 3:00 confirmation
                window elapsed, OR the address/quote changed. Both mean
                the same thing to the customer (nothing was charged, the
                fee must be re-shown), so the wording covers both without
                claiming which happened. */}
            Delivery quote expired. Refresh the delivery fee to continue.
            Nothing has been charged and no order has been placed.
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="local-delivery-needs-new-quote-cancel"
              disabled={localDeliveryQuoteBusy}
              onClick={cancelLocalDeliveryNeedsNewQuote}
              className="h-11 rounded-lg border border-stone-300 text-sm font-medium text-stone-700 hover:border-stone-400 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="local-delivery-needs-new-quote-refresh"
              disabled={localDeliveryQuoteBusy}
              onClick={requestFreshLocalDeliveryQuote}
              className="h-11 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-60"
            >
              {/* Gate C5A §5.3 — `disabled` while busy is what prevents
                  a double-click from producing two provider quotes on
                  the client side; `quote_service.request_quote` and the
                  requote lease are what prevent it on the server, which
                  is the guarantee that actually holds. */}
              {localDeliveryQuoteBusy ? "Please wait…" : "Refresh delivery fee"}
            </button>
          </div>
        </div>
      </StorefrontModal>
    );
  }

  // Local Delivery Quote/Fee Checkout Enforcement — while a quote is
  // awaiting the customer's explicit accept/cancel, show ONLY the fee-
  // confirmation view in place of the normal order-summary/payment-
  // method view. An early return here (rather than a ternary wrapping
  // the whole normal body below) keeps this addition self-contained
  // and leaves the existing render path for every other mode/step
  // completely untouched.
  if (localDeliveryQuote) {
    const quoteTotal = subtotal + tax + Number(localDeliveryQuote.fee_amount || 0) + tip;
    return (
      <StorefrontModal
        testId="retail-checkout-local-delivery-quote"
        onCancel={onClose}
        title="Delivery Fee"
        description="Confirm your delivery fee to continue"
        zIndexClassName="z-50"
        maxWidthClassName="max-w-md"
      >
        <div className="text-stone-900">
          <h2 className="font-serif text-2xl">Confirm Delivery Fee</h2>
          <div className="text-xs text-stone-500 mt-0.5">
            {tenant?.name || catalog?.name || "Our Kitchen"} · Local Delivery
          </div>
          {pendingLocalDeliveryOrder?.order_code && (
            // Pre-payment order-reference fix (Aug 2026, independent-review
            // follow-up), refined by the Pre-Order Total-Disclosure
            // Correction: `pendingLocalDeliveryOrder` is now a checkout
            // DRAFT at this point, which deliberately has no order_code
            // yet (see `retail/orders.py::ORDER_STATUS_CHECKOUT_DRAFT` —
            // no customer-facing operational order number is assigned
            // until this exact confirmation). This condition is now a
            // no-op for the draft case; it only ever matches again if a
            // future caller passes an already-promoted order in here.
            // Customer-appropriate label only — never the internal id.
            <div className="text-xs text-stone-500 mt-1" data-testid="local-delivery-quote-order-reference">
              Order reference: <span className="font-mono">{pendingLocalDeliveryOrder.order_code}</span>
            </div>
          )}

          <div className="mt-4 bg-[#fdfbf0] rounded-xl border border-stone-200 p-3 space-y-1 text-sm">
            <RowLight label="Subtotal" value={formatMoney(subtotal, currency)} />
            <RowLight label={taxLabel} value={formatMoney(tax, currency)} />
            <RowLight
              label="Delivery Fee"
              value={formatMoney(localDeliveryQuote.fee_amount, localDeliveryQuote.currency || currency)}
            />
            {tip > 0 && <RowLight label="Tip" value={formatMoney(tip, currency)} />}
            <div className="flex items-center justify-between border-t-2 border-orange-500 mt-1 pt-2">
              <span className="font-bold">TOTAL DUE</span>
              <span className="font-extrabold text-xl" data-testid="local-delivery-quote-total">
                {formatMoney(quoteTotal, currency)}
              </span>
            </div>
          </div>

          {/* The timing the customer actually chose, named before they
              approve the fee. This dialog previously showed only the
              courier's own estimate, which for a SCHEDULED order reads
              like an ASAP delivery and looks identical whether the
              schedule survived or was quietly dropped. Naming the window
              here means a substitution cannot pass unnoticed. */}
          {scheduledSelection?.start && (
            <div
              className="mt-2 rounded-md bg-orange-50 border border-orange-200 px-2.5 py-1.5 text-xs font-medium text-stone-800"
              data-testid="local-delivery-quote-scheduled-window"
            >
              Scheduled delivery: {scheduledSelection.label}
            </div>
          )}
          {localDeliveryQuote.estimated_dropoff_at && (
            <div className="mt-2 text-xs text-stone-600" data-testid="local-delivery-quote-eta">
              Estimated dropoff: {new Date(localDeliveryQuote.estimated_dropoff_at).toLocaleString()}
            </div>
          )}
          {/* Gate C5A — the customer's 3:00 decision window.

              The deadline rendered here is the SERVER's
              (`customer_decision_deadline`, stored on the quote row when
              it was minted), never "3 minutes from when this component
              mounted". A backgrounded tab or a wrong device clock can
              make this display wrong; it can never buy acceptance time,
              because the accept endpoint re-reads the same stored
              deadline. */}
          {localDeliveryQuote.customer_decision_deadline && (
            <div className="mt-2 flex items-center gap-2" data-testid="local-delivery-quote-decision">
              <span className="text-xs text-stone-600">Confirm this delivery fee within</span>
              <QuoteCountdown
                deadline={localDeliveryQuote.customer_decision_deadline}
                testId="local-delivery-decision-countdown"
              />
            </div>
          )}
          {localDeliveryQuote.preparation_minutes ? (
            <div className="mt-1 text-xs text-stone-600" data-testid="local-delivery-quote-preparation">
              Preparation time: {localDeliveryQuote.preparation_minutes} minutes
              {localDeliveryQuote.pickup_ready_at
                ? ` · ready by ${new Date(localDeliveryQuote.pickup_ready_at).toLocaleTimeString()}`
                : ""}
            </div>
          ) : null}
          {localDeliveryQuote.expires_at && (
            <div className="mt-1 text-xs text-stone-500" data-testid="local-delivery-quote-expiry">
              This quote is valid until {new Date(localDeliveryQuote.expires_at).toLocaleTimeString()}.
            </div>
          )}
          <div className="mt-2 text-xs text-stone-500" data-testid="local-delivery-confirm-explainer">
            This is your final total. Confirming places your order and starts
            card authorization — if this restaurant reviews orders before
            preparing them, your card is only charged after they accept.
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="local-delivery-quote-cancel"
              disabled={localDeliveryQuoteBusy}
              onClick={cancelLocalDeliveryQuote}
              className="h-11 rounded-lg border border-stone-300 text-sm font-medium text-stone-700 hover:border-stone-400 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="local-delivery-quote-accept"
              // Gate C5A §10.G-45 — a stale open tab cannot initiate a
              // booking. Recomputed on every render against the server
              // deadline (the countdown's 1s tick re-renders this), so
              // the control disables itself the moment the window closes
              // without needing any event to fire.
              disabled={
                localDeliveryQuoteBusy
                || secondsUntil(localDeliveryQuote.customer_decision_deadline, Date.now()) === 0
              }
              onClick={confirmLocalDeliveryQuoteAndPay}
              className="h-11 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-60"
            >
              {localDeliveryQuoteBusy ? "Please wait…" : `Place Order & Continue to Payment · ${formatMoney(quoteTotal, currency)}`}
            </button>
          </div>
        </div>
      </StorefrontModal>
    );
  }

  return (
    <StorefrontModal
      testId="retail-checkout-sheet"
      onCancel={onClose}
      onBackdropBlocked={() => logCustomerAuditEvent(slug, { event: "modal_close_blocked", orderType, reasonCode: "backdrop_click_blocked", catalogId: catalog?.id, sessionId, customerId: customer?.id })}
      title="Order Summary"
      description="Review your order and choose a payment method"
      zIndexClassName="z-50"
      maxWidthClassName="max-w-md"
    >
      <div className="text-stone-900">
        <h2 className="font-serif text-2xl">Order Summary</h2>
        <div className="text-xs text-stone-500 mt-0.5">
          {customer?.name || "Guest"} · {labelFor ? labelFor(orderType) : ({ dine_in: "Dine-In", takeout: "Takeout", pickup: "Pickup", delivery: "Local Delivery" }[orderType] || "Order")}
        </div>

        {hostDestination && (
          <div className="mt-3">
            <HostDestinationCard context={hostDestination} testId="retail-checkout-host-destination" />
          </div>
        )}

        {isOnlineCustomerPickup && (
          <div className="mt-3 bg-sky-50 border border-sky-200 rounded-lg p-3 text-sm" data-testid="retail-online-pickup-notice">
            <div className="font-medium text-sky-900">Online Customer Pickup</div>
            {catalog?.pickup_store_address?.line1 && (
              <div className="text-xs text-sky-800 mt-1" data-testid="retail-online-pickup-store-address">
                {[catalog.pickup_store_address.line1, catalog.pickup_store_address.city, catalog.pickup_store_address.state, catalog.pickup_store_address.postal_code].filter(Boolean).join(", ")}
              </div>
            )}
            {catalog?.pickup_instructions && (
              <div className="text-xs text-sky-700 mt-1" data-testid="retail-online-pickup-instructions">{catalog.pickup_instructions}</div>
            )}
            <div className="text-xs text-sky-800 mt-1" data-testid="retail-online-pickup-charge">Delivery Charge: $0.00 · Online payment required</div>
          </div>
        )}

        <div className="mt-4 bg-[#fdfbf0] rounded-xl border border-stone-200 p-3">
          <div className="text-center font-serif text-base mb-2">🍴 {tenant?.name || catalog?.name || "Our Kitchen"}</div>
          <div className="divide-y divide-stone-200/60">
            {items.map((it) => (
              <div key={it.item_id || it.id} className="flex items-center justify-between gap-2 py-1.5 text-sm">
                <span className="text-stone-700 flex items-center gap-1.5 min-w-0">
                  <span className="truncate">{it.quantity}× {itName(it)}</span>
                  {spiceOf(it) && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-300 shrink-0"
                      data-testid={`retail-checkout-spice-${it.item_id || it.id}`}>
                      🌶 {spiceOf(it).label}
                    </span>
                  )}
                  {/* Item-level Takeout marker (Sep 2026) — carried on the
                      server-side cart line, so it is already part of the
                      authoritative snapshot this checkout will turn into
                      an order. Informational: it changes nothing about
                      the totals below or the order's own mode. */}
                  {it.item_takeout && (
                    <TakeoutBadge testId={`retail-checkout-item-takeout-${it.item_id || it.id}`} />
                  )}
                </span>
                <span className="text-stone-900 font-medium shrink-0">{formatMoney(itLineTotal(it), currency)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CP2.2 — Ship to My Address flow. Renders address form + rate
            picker only when this mode is selected. Existing local
            `delivery` and other fulfillment modes are unaffected. */}
        {orderType === "shipping" && (
          <div className="mt-4 rounded-xl border border-stone-200 p-3">
            <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-2">
              Ship to my address
            </div>
            <ShipToAddressPanel
              slug={slug}
              cart={items.map((it) => ({
                // Parcel Shipping Readiness & Checkout Completion — Phase 6.
                // Root cause of the "empty_or_unknown_cart" rate-quote
                // failure: this used to send `it.item_id` (the cart LINE's
                // own random id, from retail/cart.py::_project_line_item,
                // used only to add/remove/update that line) mislabeled as
                // `product_id`. The public rate proxy looks products up by
                // `retail_products.id`, which is `it.product_id` on every
                // cart line — a completely different value — so the lookup
                // always came back empty. `it.id` (the other half of the
                // old `||` fallback) doesn't exist on a cart line at all.
                product_id: it.product_id,
                qty: Number(it.quantity || 1),
              }))}
              fulfillmentMode={orderType}
              catalogId={catalog?.id}
              sessionId={sessionId}
              initialAddress={shipToAddress}
              onAddressChange={onShippingAddressChange}
              onSelectionChange={handleShippingSelectionChange}
            />
            {shippingSelection && (
              <div
                className="mt-2 text-xs text-stone-600"
                data-testid="ship-selection-summary"
              >
                Selected: {shippingSelection.service_name} · {formatMoney(shippingSelection.rate_amount, shippingSelection.currency)}
              </div>
            )}
          </div>
        )}

        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-2">Add a tip — Thank you! 🙏</div>
          <div className="grid grid-cols-5 gap-2">
            {TIP_OPTIONS.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => { setTipPct(o.v); setCustomTip(""); }}
                data-testid={`retail-tip-${Math.round(o.v*100)}`}
                className={"h-10 rounded-lg border text-sm font-medium transition " + (tipPct === o.v ? "bg-orange-500 text-white border-orange-500" : "bg-white text-stone-700 border-stone-300 hover:border-stone-400")}
              >
                {o.l}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setTipPct("custom")}
              data-testid="retail-tip-custom"
              className={"h-10 rounded-lg border text-sm font-medium transition " + (tipPct === "custom" ? "bg-orange-500 text-white border-orange-500" : "bg-white text-stone-700 border-stone-300 hover:border-stone-400")}
            >
              Custom
            </button>
          </div>
          {tipPct === "custom" && (
            <input
              type="number"
              min="0"
              step="0.50"
              value={customTip}
              onChange={(e) => setCustomTip(e.target.value)}
              placeholder="Tip amount"
              className="mt-2 w-full h-10 rounded-lg border border-stone-300 px-3 text-sm"
            />
          )}
        </div>

        <div className="mt-4 space-y-1 text-sm">
          <RowLight label="Subtotal" value={formatMoney(subtotal, currency)} />
          {Number(serverSummary?.discount) > 0 && (
            <RowLight label="Discount" value={`- ${formatMoney(serverSummary.discount, currency)}`} />
          )}
          {Number(serverSummary?.order_discount) > 0 && (
            <RowLight label="Order Discount" value={`- ${formatMoney(serverSummary.order_discount, currency)}`} />
          )}
          <RowLight label={taxLabel} value={formatMoney(tax, currency)} />
          {delivery > 0 && (
            <RowLight label="Delivery" value={formatMoney(delivery, currency)} />
          )}
          {isOnlineCustomerPickup && (
            <RowLight label="Delivery Charge" value={formatMoney(0, currency)} />
          )}
          {orderType === "shipping" && shippingSelection && (
            <RowLight label="Shipping" value={formatMoney(shippingCharge, shippingSelection.currency || currency)} />
          )}
          <RowLight label="Tip" value={formatMoney(tip, currency)} />
          <div className="flex items-center justify-between border-t-2 border-orange-500 mt-1 pt-2">
            <span className="font-bold">ORDER TOTAL</span>
            <span className="font-extrabold text-xl" data-testid="retail-summary-total">{formatMoney(total, currency)}</span>
          </div>
          {summaryError && (
            <div className="text-xs text-red-600" data-testid="retail-summary-error">{summaryError}</div>
          )}
          {summaryPending && !summaryReady && items.length > 0 && (
            <div className="text-xs text-stone-500" data-testid="retail-summary-loading">Calculating your total…</div>
          )}
          {loyaltyValue > 0 && (
            <>
              <RowLight label="Loyalty points" value={`- ${formatMoney(loyaltyValue, currency)}`} />
              <div className="flex items-center justify-between border-t mt-1 pt-2">
                <span className="font-bold">TOTAL DUE</span>
                <span className="font-extrabold text-xl" data-testid="retail-summary-amount-due">
                  {formatMoney(amountDue, currency)}
                </span>
              </div>
            </>
          )}
          {/* `catalog?.id` alone is undefined on a checkout reopened from
              a persisted cart, and `catalog_id` is REQUIRED by the
              backend model — so the panel falls back to the cart's own
              catalog id, exactly as VerificationModal below already
              does. Without it the request is a guaranteed 422. */}
          {loyaltyVerifyNeeded && (
            <div className="border-t pt-3 mt-3" data-testid="loyalty-verify-cta">
              <button
                type="button"
                onClick={() => setLoyaltyVerifyOpen(true)}
                data-testid="loyalty-verify-cta-btn"
                className="w-full h-11 rounded-lg border border-emerald-300 bg-emerald-50/60 text-sm font-semibold text-emerald-900 hover:bg-emerald-100"
              >
                Verify to view and use loyalty points
              </button>
              <p className="mt-1 text-center text-xs text-stone-500">
                We&apos;ll send a 6-digit code. Your points stay private until you verify.
              </p>
            </div>
          )}
          <LoyaltyRedeemPanel
            slug={slug}
            catalogId={catalog?.id || cart?.catalog_id}
            sessionId={sessionId}
            verification={loyaltyVerification}
            points={loyaltyPoints}
            onChange={setLoyaltyPoints}
            onSummary={(s) => {
              setLoyaltySummary(s);
              // No summary means no priced redemption. Drop the proposal
              // too, so nothing stale is submitted or displayed.
              if (!s) setLoyaltyPoints(0);
            }}
          />
        </div>

        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-2">Choose Payment Method</div>
          {/* Sprint 1 Go-Live (Feb 2026) — Delivery orders are
              card-only per V1 policy. Cash is hidden entirely for the
              delivery flow because the driver has no cash-drawer.
              Pickup / Dine-in keep both options unchanged.
              USPS Production Enablement M1 (Scope E) — shipping orders
              are card-only for the same reason. */}
          <div className={"grid gap-3 " + (
            paymentTiles.length >= 3 ? "grid-cols-3"
              : paymentTiles.length === 2 ? "grid-cols-2"
              : "grid-cols-1"
          )}>
            {paymentTiles
              .map((o) => (
              <button
                key={o.v}
                type="button"
                disabled={!!o.disabled}
                aria-disabled={!!o.disabled}
                onClick={() => { if (!o.disabled) setPaymentMethod(o.v); }}
                data-testid={`retail-payment-${o.v}`}
                className={
                  "h-20 rounded-lg border-2 text-sm font-medium transition flex flex-col items-center justify-center gap-1 " +
                  (o.disabled
                    ? o.bg + " opacity-40 cursor-not-allowed"
                    : paymentMethod === o.v ? "ring-2 ring-orange-500 " + o.bg : o.bg + " opacity-80 hover:opacity-100")
                }
              >
                <span className="text-2xl">{o.icon}</span>
                <span>{o.l}</span>
              </button>
            ))}
          </div>
          {/* Kiosk No-Fallback Rule — say WHY card payment cannot be
              taken, rather than leaving a dead tile unexplained. The
              sentence is the backend's own customer-safe copy for the
              specific reason (reader disconnected, agent offline,
              firmware updating, device not registered), so staff and
              customer see the same actionable thing. */}
          {kioskReaderTenant && !kioskReaderReady && (
            <div
              className="mt-2 text-xs text-stone-600 border rounded-md bg-amber-50/60 border-amber-200 px-3 py-2"
              data-testid="kiosk-reader-unavailable"
              role="status"
            >
              {stripeTerminalMessage
                || "Card payment isn't available at this counter right now. "
                   + "Please pay with cash or ask a member of staff."}
            </div>
          )}
        </div>

        {fulfillmentApplies && (
          <div className="mt-4" data-testid="retail-fulfillment-section">
            <FulfillmentTimePicker
              availability={fulfillmentAvailability}
              value={scheduledSelection}
              onChange={setScheduledSelection}
              labelForMode={labelFor ? labelFor(orderType) : orderType}
            />
          </div>
        )}
        {shippingMethodMissing && (
          <div
            className="mt-4 text-center text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
            data-testid="retail-shipping-method-required-hint"
            role="status"
          >
            Select a shipping method before continuing.
          </div>
        )}
        <button
          type="button"
          disabled={submitting || items.length === 0 || (!!summaryError && !summaryPending) || shippingMethodMissing || fulfillmentBlocked || scheduledWindowMissing}
          onClick={submit}
          data-testid="retail-place-order-btn"
          className={
            "mt-3 w-full h-12 rounded-lg font-semibold text-white " +
            (submitting || items.length === 0 || shippingMethodMissing || fulfillmentBlocked || scheduledWindowMissing
              ? "bg-orange-500/40 cursor-not-allowed"
              : "bg-orange-500 hover:bg-orange-600 active:scale-[0.99]")
          }
        >
          {submitting
            ? (orderType === "delivery" ? "Getting delivery total…" : "Placing order…")
            : fulfillmentBlocked
            ? "Currently unavailable"
            : scheduledWindowMissing
            ? "Choose a time to continue"
            : shippingMethodMissing
            ? "Select a shipping method to continue"
            : paymentMethod === "clover_terminal"
            ? `Place Order – Pay at Terminal · ${formatMoney(total, currency)}`
            : paymentMethod === "stripe_terminal"
            ? `Place Order – Pay by Card · ${formatMoney(amountDue, currency)}`
            : (paymentMethod === "cash" && orderType === "dine_in")
            ? `Place Order – Pay at Counter · ${formatMoney(amountDue, currency)}`
            : paymentMethod === "cash"
            ? `Place Order · ${formatMoney(amountDue, currency)}`
            : // Pre-Order Total-Disclosure Correction (Aug 2026) — this
              // view only ever renders BEFORE a Local Delivery quote
              // exists (once one does, the "Confirm Delivery Fee" early
              // return above takes over). `total` here is merchandise-
              // only and does NOT include the delivery fee the backend
              // will actually charge, so this button must never claim
              // to place an order at that figure. The real, complete
              // total is shown — and confirmed — on the next screen.
              orderType === "delivery"
            ? "Get Delivery Total"
            : !isFullyVerified
            ? `Verify Email & Place Order · ${formatMoney(amountDue, currency)}`
            : `Place Order · ${formatMoney(amountDue, currency)}`}
        </button>
        {orderType === "delivery" && paymentMethod === "card" && (
          <div className="mt-2 text-center text-xs text-stone-500" data-testid="retail-delivery-total-explainer">
            This calculates your delivery fee — it doesn&apos;t place your order yet.
            You&apos;ll see and confirm the full total, including delivery, before paying.
          </div>
        )}
        {!isFullyVerified && paymentMethod !== "cash" && requiredChannels.length > 0 && (
          <div className="mt-2 text-center text-xs text-stone-500" data-testid="retail-verification-hint">
            We&apos;ll send a 6-digit code to your email to verify it&apos;s you.
          </div>
        )}
        {paymentMethod === "cash" && orderType === "dine_in" && (
          <div className="mt-2 text-center text-xs text-stone-500" data-testid="retail-pay-at-counter-hint">
            Your order will be placed. Please pay at the counter and wait for staff confirmation.
          </div>
        )}
        <button
          type="button"
          onClick={onClose}
          data-testid="retail-back-to-menu"
          className="mt-2 w-full h-11 rounded-lg border border-stone-300 text-sm text-stone-600 hover:bg-stone-50"
        >
          ← Back to Menu
        </button>
      </div>

      {/* Phase 1 (Modal Reliability) — VerificationModal and
          CloverTerminalPaymentDialog are their own top-level dialogs (each
          owns its own overlay/focus-trap). Radix Dialogs nest safely, so
          they render here, inside the StorefrontModal's Portal content but
          outside the Order Summary card itself — matching their previous
          position as siblings of the card. */}
      {verificationOpen && (
        <VerificationModal
          slug={slug}
          catalogId={catalog?.id || cart?.catalog_id}
          sessionId={sessionId}
          customer={customer}
          requiredChannels={requiredChannels}
          orderType={orderType}
          onClose={() => setVerificationOpen(false)}
          onVerified={(toks) => {
            setVerifiedTokens(toks);
            setVerificationOpen(false);
            setPendingSubmitAfterVerify(true);
          }}
        />
      )}
      {/* Walk-in loyalty verification (Sep 2026) — the EXISTING OTP
          component and the existing backend flow, nothing new.
          `purpose="order_lookup"` is this repo's one value that forces a
          REQUIRED verdict regardless of the tenant's checkout OTP policy
          (`routes/retail_otp.py`, "order_lookup_gate"); loyalty needs
          exactly that guarantee for the same reason Past Orders does, so
          it reuses it rather than inventing a purpose.

          It mounts only on the customer's own click, so nothing is ever
          sent merely because a phone number reached ten digits, and the
          modal issues at most one code per attempt (its own
          `issuedForIdxRef` guard). If the backend already holds a valid
          verified grant for this destination it returns
          `already_verified` and sends NO new code at all. SMS-consent
          rules are untouched: `customer` is passed through verbatim and
          the backend remains the authority, with the modal's own inline
          "check the box, then send" recovery unchanged. */}
      {loyaltyVerifyOpen && (
        <VerificationModal
          slug={slug}
          catalogId={catalog?.id || cart?.catalog_id}
          sessionId={sessionId}
          customer={customer}
          requiredChannels={[loyaltyVerifyChannel]}
          orderType={orderType}
          purpose="order_lookup"
          phoneAccessAllowed={!storefrontRequiresLoyaltyOtp(catalog)}
          onClose={() => setLoyaltyVerifyOpen(false)}
          onVerified={(toks) => {
            setLoyaltyVerifyOpen(false);
            const verified = toks?.[loyaltyVerifyChannel];
            if (!verified?.token) return;
            // Deliberately NOT `setVerifiedTokens`. That map is the
            // order-SUBMISSION gate; feeding it here would let proving
            // identity for points also satisfy the gate that decides
            // whether an order may be placed at all. The proof goes up
            // to the page instead, which owns its lifetime — session
            // expiry, New Order, identity change, tenant/catalog change
            // all clear it there, in one place.
            onLoyaltyVerified?.({
              channel: loyaltyVerifyChannel,
              token: verified.token,
              destinationValue: loyaltyVerifyChannel === "sms"
                ? (customer?.phone || null) : (customer?.email || null),
              catalogId: catalog?.id || cart?.catalog_id,
              verifiedAt: verified.verifiedAt || new Date().toISOString(),
            });
          }}
        />
      )}
      {cloverDialogOpen && cloverActiveOrder && (
        <CloverTerminalPaymentDialog
          orderId={cloverActiveOrder.id || cloverActiveOrder.order_code}
          open={cloverDialogOpen}
          authToken={null}
          resumeSessionId={readRecovery()?.sessionId || null}
          onSession={(sess) => {
            // Enrich the recovery pointer with the resolved session_id.
            if (sess?.session_id) {
              saveRecovery(
                cloverActiveOrder.id || cloverActiveOrder.order_code,
                sess.session_id,
              );
            }
          }}
          onDone={(status, _session) => {
            setCloverDialogOpen(false);
            // Recovery-pointer cleanup rules (spec Part 8):
            //   * Clear on: paid, cancelled, expired.
            //   * KEEP on: reconciliation (operator must resolve),
            //     declined (customer may want to retry — dialog will
            //     handle retry rules), any transient network error.
            const CLEAR_ON = new Set(["paid", "cancelled", "expired"]);
            if (CLEAR_ON.has(status)) clearRecovery();

            if (status === "paid") {
              onPlaced?.(cloverActiveOrder);
            } else if (status === "reconciliation") {
              toast.error("Payment status requires staff assistance. Please ask an employee.");
            } else if (status === "declined") {
              toast.error("Card was declined. You can try again or choose another payment method.");
            } else if (status === "cancelled") {
              toast.info("Payment was cancelled.");
            } else if (status === "expired") {
              toast.error("The payment timed out. Please try again.");
            } else {
              toast.error("Payment did not complete. Please retry or ask staff for help.");
            }
            setCloverActiveOrder(null);
            setSubmitting(false);
          }}
        />
      )}
      {stripeTerminalDialogOpen && stripeTerminalActiveOrder && (
        <StripeTerminalPaymentDialog
          slug={slug}
          orderId={stripeTerminalActiveOrder.id || stripeTerminalActiveOrder.order_code}
          // These routes authorize a real charge and accept `order_code`
          // (which is sequential and printed on the customer's receipt),
          // so the per-order credential is mandatory — the same gate
          // `routes/payments.py::create_session` was hardened to require.
          orderAccessToken={loadOrderAccessToken(
            slug, stripeTerminalActiveOrder.order_code,
          )}
          open={stripeTerminalDialogOpen}
          resumeSessionId={readStripeRecovery()?.sessionId || null}
          onSession={(sess) => {
            if (sess?.session_id) {
              saveStripeRecovery(
                stripeTerminalActiveOrder.id || stripeTerminalActiveOrder.order_code,
                sess.session_id,
              );
            }
          }}
          onDone={(status, _session) => {
            setStripeTerminalDialogOpen(false);
            // Same pointer-cleanup rules as the Clover flow:
            //   * Clear on: paid, cancelled, expired.
            //   * KEEP on: reconciliation (staff must resolve — and the
            //     customer may well have been charged), declined (the
            //     customer may retry), and any transient network error.
            const CLEAR_ON = new Set(["paid", "cancelled", "expired"]);
            if (CLEAR_ON.has(status)) clearStripeRecovery();

            if (status === "paid") {
              onPlaced?.(stripeTerminalActiveOrder);
            } else if (status === "reconciliation") {
              toast.error("Payment status requires staff assistance. Please ask an employee.");
            } else if (status === "declined") {
              toast.error("Card was declined. You can try again or choose another payment method.");
            } else if (status === "cancelled") {
              toast.info("Payment was cancelled.");
            } else if (status === "expired") {
              toast.error("The payment timed out. Please try again.");
            } else {
              toast.error("Payment did not complete. Please retry or ask staff for help.");
            }
            setStripeTerminalActiveOrder(null);
            setSubmitting(false);
          }}
        />
      )}
    </StorefrontModal>
  );
}
